import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { ArrowLeft, FileSpreadsheet, Play, Eye, Download, Filter, CheckCircle, Clock, FileCheck, Upload, Printer, XCircle, AlertTriangle, Wrench, RotateCcw, Edit, History, Calendar, Package, PackageCheck, TrendingUp, UserCheck, Users, List, Trash2, Search, Square, CheckSquare, Link2Off as LinkIcon, Loader2, Send, Settings2 } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Badge } from '@/components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Checkbox } from '@/components/ui/checkbox';
import { toast } from 'sonner';
import apiClient from '@/services/apiClient';
import { usePricingChannels } from '@/hooks/usePricingChannels';
import * as authApi from '@/services/authApi';
import * as printerApi from '@/services/printerApi';
import { PRINTER_CONFIGS } from '@/utils/tsplGenerator';
import { runPickSlipRestickerPipeline } from '@/utils/pickSlipResticker';
import ErpConvertDialog from './components/ErpConvertDialog';
import ErpStockPointsDialog from './components/ErpStockPointsDialog';

// 2026-05-29 — Pick Slip cancellation opened up to all authenticated
// users. Backend role gate was removed in `routes/wms.py`; the
// `Admin`-only `force_cancel` flag (bypasses DC_LINKED / PACKED
// safeguards) is still enforced server-side per-endpoint.
const CANCEL_ALLOWED_ROLES = null;  // null == any logged-in user

// Roles allowed to reassign pick slips
const REASSIGN_ALLOWED_ROLES = ['Admin', 'Dispatch Supervisor', 'Dispatch Incharge', 'WMS Supervisor', 'WMS Incharge'];

// Valid cancellation reasons for single USN
const USN_CANCEL_REASONS = [
  'Wrong item picked',
  'Damaged item',
  'Customer request',
  'Quality issue',
  'Duplicate pick',
  'Other'
];

const FGPickSlips = () => {
  const navigate = useNavigate();
  const [pickSlips, setPickSlips] = useState([]);
  const [loading, setLoading] = useState(true);
  const [statusFilter, setStatusFilter] = useState('all');
  const [searchTerm, setSearchTerm] = useState('');
  const [user, setUser] = useState(null);
  
  // Date filter state
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  
  // Summary stats
  const [summaryStats, setSummaryStats] = useState({
    total_qty: 0,
    in_progress_qty: 0,
    pending_qty: 0
  });
  
  // Design Transfer slips
  const [designTransferSlips, setDesignTransferSlips] = useState([]);
  const [dtLoading, setDtLoading] = useState(true);
  
  // Detail dialog
  const [detailDialogOpen, setDetailDialogOpen] = useState(false);
  const [selectedSlip, setSelectedSlip] = useState(null);
  
  // DC dialog
  const [dcDialogOpen, setDcDialogOpen] = useState(false);
  const [dcSlipNumber, setDcSlipNumber] = useState('');
  const [erpSlip, setErpSlip] = useState(null);          // slip being converted to DC in ERP
  const [showErpSettings, setShowErpSettings] = useState(false);
  // Admin — reconcile orphaned picks (pieces stuck PICKED on a cancelled/deleted slip)
  const [showReconcile, setShowReconcile] = useState(false);
  const [reconcilePreview, setReconcilePreview] = useState(null);
  const [reconcileLoading, setReconcileLoading] = useState(false);
  const [reconcileApplying, setReconcileApplying] = useState(false);
  const [reconcileResult, setReconcileResult] = useState(null);

  const openReconcile = async () => {
    setShowReconcile(true);
    setReconcileResult(null);
    setReconcilePreview(null);
    setReconcileLoading(true);
    try {
      const res = await apiClient.get('/wms/fg/orphaned-picks/preview');
      setReconcilePreview(res.data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Failed to scan for stuck picks');
      setShowReconcile(false);
    } finally {
      setReconcileLoading(false);
    }
  };

  const runReconcile = async () => {
    setReconcileApplying(true);
    try {
      const res = await apiClient.post('/wms/fg/orphaned-picks/reconcile', { confirm: true });
      setReconcileResult(res.data);
      const n = res.data?.restored_to_stored || 0;
      const p = res.data?.sent_to_put_away || 0;
      toast.success(`Released ${n + p} stuck piece(s) — ${n} back to stock, ${p} to put-away`);
      fetchPickSlips();
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Reconcile failed');
    } finally {
      setReconcileApplying(false);
    }
  };
  const [dcNumber, setDcNumber] = useState('');
  const [transporters, setTransporters] = useState([]);
  const [selectedTransporter, setSelectedTransporter] = useState('');
  const [transporterSearch, setTransporterSearch] = useState('');
  const [recommendation, setRecommendation] = useState(null);
  const [loadingReco, setLoadingReco] = useState(false);
  
  // Export dialog
  const [exportDialogOpen, setExportDialogOpen] = useState(false);
  const [exportStartDate, setExportStartDate] = useState('');
  const [exportEndDate, setExportEndDate] = useState('');
  const [exportInfo, setExportInfo] = useState(null);
  const [exportLoading, setExportLoading] = useState(false);
  
  // Cancel dialog
  const [cancelDialogOpen, setCancelDialogOpen] = useState(false);
  const [cancelSlipNumber, setCancelSlipNumber] = useState('');
  const [cancellationReason, setCancellationReason] = useState('');
  const [cancelSubmitting, setCancelSubmitting] = useState(false);
  const [forceCancel, setForceCancel] = useState(false);  // Admin-only: bypass DC_LINKED/PACKED checks
  const [cancelSlipStatus, setCancelSlipStatus] = useState('');  // Track the status of slip being cancelled
  
  // Success confirmation dialog (for printout)
  const [successDialogOpen, setSuccessDialogOpen] = useState(false);
  const [cancelledSlipInfo, setCancelledSlipInfo] = useState(null);
  
  // Repair statuses
  const [repairLoading, setRepairLoading] = useState(false);
  const [repairDialogOpen, setRepairDialogOpen] = useState(false);
  const [repairResult, setRepairResult] = useState(null);
  
  // Rollback
  const [rollbackLoading, setRollbackLoading] = useState(false);
  
  // Push to queue
  const [pushingToQueue, setPushingToQueue] = useState(null);
  
  // Reopen pick slip (Admin only)
  const [reopenDialogOpen, setReopenDialogOpen] = useState(false);
  const [reopenSlipNumber, setReopenSlipNumber] = useState('');
  const [reopenReason, setReopenReason] = useState('');
  const [reopenSubmitting, setReopenSubmitting] = useState(false);

  // 2026-05-14 — Re-arm pack print state (Admin only). For half-printed
  // re-sticker batches: clears `restickered_at` on every fg-inventory
  // pack of the slip so the Print Sticker per-SKU buttons treat them
  // as fresh. Does NOT touch slip status / USN replacement chains.
  const [reArmDialogOpen, setReArmDialogOpen] = useState(false);
  const [reArmSlipNumber, setReArmSlipNumber] = useState('');
  const [reArmReason, setReArmReason] = useState('');
  const [reArmSubmitting, setReArmSubmitting] = useState(false);
  const [reArmResult, setReArmResult] = useState(null);


  // Reassign pick slip state
  const [reassignDialogOpen, setReassignDialogOpen] = useState(false);
  const [reassignSlipNumber, setReassignSlipNumber] = useState('');
  const [reassignSlipInfo, setReassignSlipInfo] = useState(null);
  const [reassignReason, setReassignReason] = useState('');
  const [reassignSubmitting, setReassignSubmitting] = useState(false);
  const [staffList, setStaffList] = useState([]);
  const [selectedNewPicker, setSelectedNewPicker] = useState('');
  const [staffSearchTerm, setStaffSearchTerm] = useState('');
  
  // Reassignment history dialog
  const [historyDialogOpen, setHistoryDialogOpen] = useState(false);
  const [reassignmentHistory, setReassignmentHistory] = useState(null);

  // Cancel Single USN dialog
  const [cancelUSNDialogOpen, setCancelUSNDialogOpen] = useState(false);
  const [cancelUSNSlipNumber, setCancelUSNSlipNumber] = useState('');
  const [pickedUSNs, setPickedUSNs] = useState([]);
  const [usnPickPack, setUsnPickPack] = useState(false);
  const [cancelledUSNs, setCancelledUSNs] = useState([]);
  const [selectedUSNsToCancel, setSelectedUSNsToCancel] = useState([]); // Multi-select
  const [cancelUSNReason, setCancelUSNReason] = useState('');
  const [cancelUSNCustomReason, setCancelUSNCustomReason] = useState('');
  const [cancelUSNSubmitting, setCancelUSNSubmitting] = useState(false);
  const [loadingUSNs, setLoadingUSNs] = useState(false);
  const [usnSearchTerm, setUsnSearchTerm] = useState(''); // Search filter

  // ============================================================
  // 2026-05-13 — Print Sticker per SKU (urgent-dispatch resticker)
  // ============================================================
  const [usbDevice, setUsbDevice] = useState(null);
  const [printerModel] = useState('TSC_TE210');
  const [templates, setTemplates] = useState([]);
  const [printingSku, setPrintingSku] = useState(null);   // sku currently being printed
  // 2026-05-18 — Force reprint toggle. When ticked, the pipeline ignores
  // each pack's `restickered_at` flag and reprints every pack on the
  // slip even if it has already been re-stickered. Defaults to off so
  // the normal CSV-revision idempotency stays in place.
  const [forceReprint, setForceReprint] = useState(false);
  const [printLog, setPrintLog] = useState([]);            // [{ts, msg}]
  // 2026-05-14 — Per-row revert state. Admin-only "Revert Row" button on
  // each SKU row in the slip detail dialog clears restickered_at for
  // every pack of that SKU on that slip. Distinct from the legacy
  // "Revert Done" which would dump every restickered pack across the
  // whole slip even if 4 of 10 rows had completed cleanly.
  const [revertingSku, setRevertingSku] = useState(null);
  const [printResultDialogOpen, setPrintResultDialogOpen] = useState(false);
  const [printResult, setPrintResult] = useState(null);
  // 2026-05-14 — Two-phase combo printing. After product stickers print,
  // operator must swap printer/roll to box-sticker stock before the box
  // stickers print. Pipeline pauses + invokes onPhaseTransition; we open
  // this dialog and resolve its Promise on Continue/Cancel click.
  // 2026-05-14 (B) — Box-sticker printer must be EXPLICITLY selected on
  // this prompt (operator usually swaps to a different printer / roll
  // for box labels). The dialog tracks the newly-picked device locally
  // and only enables Continue when one is chosen.
  const [phaseDialogOpen, setPhaseDialogOpen] = useState(false);
  const [phaseDialogComboCount, setPhaseDialogComboCount] = useState(0);
  const [phaseDialogResolve, setPhaseDialogResolve] = useState(null);
  const [phaseBoxDevice, setPhaseBoxDevice] = useState(null);
  const [phaseBoxConnecting, setPhaseBoxConnecting] = useState(false);
  // 2026-05-14 (C) — Template selector on the Phase-B prompt. The
  // operator can override the auto-resolved box template if alignment
  // is wrong on the loaded roll. `phaseBoxAvailableTemplates` is
  // populated from the pipeline's onPhaseTransition payload.
  const [phaseBoxTemplate, setPhaseBoxTemplate] = useState('');
  const [phaseBoxAvailableTemplates, setPhaseBoxAvailableTemplates] = useState([]);

  // Admin — add a piece to an already-closed carton
  const [addBoxDialogOpen, setAddBoxDialogOpen] = useState(false);
  const [addBoxSlip, setAddBoxSlip] = useState(null);
  const [addBoxList, setAddBoxList] = useState([]);
  const [addBoxSelectedId, setAddBoxSelectedId] = useState('');
  const [addBoxUsn, setAddBoxUsn] = useState('');
  const [addBoxBusy, setAddBoxBusy] = useState(false);
  const [addBoxLoading, setAddBoxLoading] = useState(false);
  const [confirmDeleteBoxId, setConfirmDeleteBoxId] = useState(null);
  const [deletingBoxId, setDeletingBoxId] = useState(null);

  // 2026-05-14 (C) — Phase A (pre-print) prompt. The operator confirms
  // the product printer and (optionally) overrides the auto-resolved
  // product template before any TSPL hits the wire. Same template list
  // is used in both prompts.
  const [phaseAOpen, setPhaseAOpen] = useState(false);
  const [phaseAResolve, setPhaseAResolve] = useState(null);
  const [phaseAPackCount, setPhaseAPackCount] = useState(0);
  const [phaseAComboCount, setPhaseAComboCount] = useState(0);
  const [phaseAProductTemplate, setPhaseAProductTemplate] = useState('');
  const [phaseAAvailableTemplates, setPhaseAAvailableTemplates] = useState([]);
  const [phaseADevice, setPhaseADevice] = useState(null);
  const [phaseAConnecting, setPhaseAConnecting] = useState(false);
  // 2026-05-14 — pricing_type state code drives `rates_master` lookup for
  // quality/color/size resolution on the printed sticker. Default 'TN'
  // mirrors FG Re-Sticker Queue. Operator can override per slip via the
  // dropdown in the Detail Dialog.
  const [pricingType, setPricingType] = useState('TN');
  const pricingChannels = usePricingChannels();

  // 2026-05-14 — Robust template loader. The original `useEffect` swallowed
  // every error silently ⇒ when the on-mount fetch hit a 401 race (auth
  // not yet ready) or a transient network blip, `templates` stayed empty
  // forever and the operator only saw "Label templates not loaded yet"
  // when they clicked Print. Now: surface real errors + retry on demand.
  const fetchLabelTemplates = useCallback(async () => {
    const res = await apiClient.get('/main-labels/templates');
    const list = res.data?.templates || [];
    setTemplates(list);
    return list;
  }, []);

  useEffect(() => {
    fetchLabelTemplates().catch((e) => {
      console.warn('FGPickSlips: initial templates fetch failed (will retry on demand)', e);
    });
  }, [fetchLabelTemplates]);

  const ensureUSBPrinter = async () => {
    if (usbDevice) return usbDevice;
    if (!printerApi.isWebUSBSupported()) {
      toast.error('WebUSB not supported in this browser — use Chrome/Edge on desktop');
      return null;
    }
    try {
      const dev = await printerApi.requestUSBPrinter();
      setUsbDevice(dev);
      return dev;
    } catch (e) {
      toast.error(`Printer connect failed: ${e.message || e}`);
      return null;
    }
  };

  const handlePrintStickerForSku = async (skuCode) => {
    if (!selectedSlip?.pick_slip_number) return;
    // 2026-05-14 — Retry-on-demand if templates didn't load on mount
    // (covers the 401 race / first-load timeout the operator hit on
    // production). Surface the actual error if the retry also fails so
    // we stop hiding behind a misleading "please retry in a moment".
    let tpls = templates;
    if (!tpls.length) {
      const toastId = toast.loading('Loading label templates…');
      try {
        tpls = await fetchLabelTemplates();
      } catch (e) {
        toast.dismiss(toastId);
        toast.error(`Label templates failed to load: ${e?.response?.data?.detail || e?.message || e}`);
        return;
      }
      toast.dismiss(toastId);
      if (!tpls.length) {
        toast.error('No label templates configured. Set up templates in Admin → Main Labels first.');
        return;
      }
    }
    const dev = await ensureUSBPrinter();
    if (!dev) return;
    setPrintingSku(skuCode);
    setPrintLog([{ ts: Date.now(), msg: `Starting re-sticker for SKU ${skuCode}…` }]);
    try {
      const result = await runPickSlipRestickerPipeline({
        pickSlipNumber: selectedSlip.pick_slip_number,
        skuCode,
        section: null,
        staffId: null,
        staffName: null,
        usbDevice: dev,
        printerConfig: PRINTER_CONFIGS[printerModel] || PRINTER_CONFIGS.TSC_TE210,
        templates: tpls,
        apiBase: process.env.REACT_APP_BACKEND_URL || '',
        sessionToken: localStorage.getItem('session_token') || '',
        siteId: localStorage.getItem('active_site_id') || 'salem',
        pricingType,
        forceReprint,
        onLog: (msg) => setPrintLog((prev) => [{ ts: Date.now(), msg }, ...prev].slice(0, 50)),
        onPhaseStart: ({ packCount, comboCount, suggestedProductTemplate, availableTemplates }) => new Promise((resolve) => {
          setPhaseAPackCount(packCount || 0);
          setPhaseAComboCount(comboCount || 0);
          setPhaseAProductTemplate(suggestedProductTemplate || '');
          setPhaseAAvailableTemplates(availableTemplates || []);
          setPhaseADevice(usbDevice || null);
          setPhaseAResolve(() => resolve);
          setPhaseAOpen(true);
        }),
        onPhaseTransition: ({ comboCount, suggestedBoxTemplate, availableTemplates }) => new Promise((resolve) => {
          setPhaseDialogComboCount(comboCount || 0);
          setPhaseBoxTemplate(suggestedBoxTemplate || '');
          setPhaseBoxAvailableTemplates(availableTemplates || []);
          setPhaseDialogResolve(() => resolve);
          setPhaseDialogOpen(true);
        }),
      });
      setPrintResult({ skuCode, ...result });
      setPrintResultDialogOpen(true);
      if (result.marked_count > 0) {
        toast.success(
          `Printed ${result.marked_count} sticker(s) for SKU ${skuCode}`
          + (result.skipped ? `, skipped ${result.skipped} already-restickered` : '')
        );
      } else if (result.skipped) {
        toast.info(`All ${result.skipped} pack(s) of SKU ${skuCode} already restickered`);
      }
      // Refresh slip detail so the row reflects restickered state.
      try {
        const refreshed = await apiClient.get(`/wms/fg/pick-slips/${selectedSlip.pick_slip_number}`);
        setSelectedSlip(refreshed.data);
      } catch (e) { /* non-fatal */ }
    } catch (e) {
      console.error('Print Sticker pipeline failed', e);
      toast.error(`Print Sticker failed: ${e.message || e}`, { duration: 12000 });
      setPrintResult({ skuCode, success: false, error: e.message || String(e) });
      setPrintResultDialogOpen(true);
    } finally {
      setPrintingSku(null);
    }
  };

  // 2026-05-14 — Per-row revert. Admin-only. Clears restickered_at on
  // every pack of `skuCode` on the currently-open slip. Backend route:
  // POST /price-revision/revert-pick-slip-row.
  const handleRevertPickSlipRow = async (skuCode) => {
    if (!selectedSlip?.pick_slip_number) return;
    if (!window.confirm(
      `Revert re-sticker status for SKU ${skuCode} on slip `
      + `${selectedSlip.pick_slip_number}? The Print Sticker button will be `
      + `re-armed for this row only. Other re-stickered rows on the slip are untouched.`
    )) return;
    setRevertingSku(skuCode);
    try {
      const res = await apiClient.post('/price-revision/revert-pick-slip-row', {
        pick_slip_number: selectedSlip.pick_slip_number,
        sku_code: skuCode,
        section: null,
        staff_id: null,
        staff_name: null,
        reason: 'manual_row_revert',
      });
      const n = res.data?.reverted_count || 0;
      if (n === 0) {
        toast.info(res.data?.message || 'Nothing to revert on this row.');
      } else {
        toast.success(`Reverted ${n} pack(s) for SKU ${skuCode}`);
      }
      try {
        const refreshed = await apiClient.get(
          `/wms/fg/pick-slips/${selectedSlip.pick_slip_number}`
        );
        setSelectedSlip(refreshed.data);
      } catch (_e) { /* non-fatal */ }
    } catch (e) {
      toast.error(e.response?.data?.detail || `Revert failed: ${e.message || e}`);
    } finally {
      setRevertingSku(null);
    }
  };

  // 2026-05-29 — Cancel is now available to any authenticated user.
  // `CANCEL_ALLOWED_ROLES === null` means "no role restriction".
  const canCancel = !!user && (CANCEL_ALLOWED_ROLES === null || CANCEL_ALLOWED_ROLES.includes(user.role));
  
  // Check if current user can reassign
  const canReassign = user && REASSIGN_ALLOWED_ROLES.includes(user.role);
  
  // Check if current user is admin (for repair function)
  const isAdmin = user && user.role === 'Admin';
  // Roles allowed to manage closed cartons (add piece / delete box)
  const CARTON_MANAGE_ROLES = ['admin', 'super admin', 'super_admin', 'supervisor', 'floor supervisor', 'section incharge', 'incharge', 'floor incharge'];
  const canManageCarton = !!user && CARTON_MANAGE_ROLES.includes((user.role || '').toLowerCase());

  useEffect(() => {
    fetchUser();
    fetchPickSlips();
    fetchDesignTransferSlips();
    fetchSummaryStats();
  }, [statusFilter, startDate, endDate]);

  const fetchUser = async () => {
    try {
      const userData = await authApi.getMe();
      setUser(userData);
    } catch (error) {
      console.error('Error fetching user:', error);
    }
  };

  const fetchSummaryStats = async () => {
    try {
      let url = '/wms/fg/pick-slips/summary';
      const params = [];
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      if (params.length > 0) url += '?' + params.join('&');
      
      const response = await apiClient.get(url);
      setSummaryStats(response.data);
    } catch (error) {
      console.error('Failed to fetch summary stats:', error);
    }
  };

  const fetchPickSlips = async () => {
    try {
      setLoading(true);
      const params = [];
      if (statusFilter !== 'all') params.push(`status=${statusFilter}`);
      if (startDate) params.push(`start_date=${startDate}`);
      if (endDate) params.push(`end_date=${endDate}`);
      // Exclude packed by default (handled by backend)
      params.push('exclude_packed=true');
      
      const queryString = params.length > 0 ? '?' + params.join('&') : '';
      const response = await apiClient.get(`/wms/fg/pick-slips${queryString}`);
      setPickSlips(response.data.pick_slips || []);
    } catch (error) {
      toast.error('Failed to fetch pick slips');
    } finally {
      setLoading(false);
    }
  };

  const fetchDesignTransferSlips = async () => {
    try {
      setDtLoading(true);
      const params = statusFilter !== 'all' ? `?status=${statusFilter}` : '';
      const response = await apiClient.get(`/wms/fg/design-transfer-slips${params}`);
      setDesignTransferSlips(response.data.slips || []);
    } catch (error) {
      console.error('Failed to fetch design transfer slips:', error);
    } finally {
      setDtLoading(false);
    }
  };

  const handleViewDetail = async (slipNumber) => {
    try {
      // Use query parameter endpoint to handle slashes in slip numbers
      const response = await apiClient.get(`/wms/fg/pick-slip-detail?slip_number=${encodeURIComponent(slipNumber)}`);
      setSelectedSlip(response.data);
      // Reset force-reprint toggle when opening a new slip so it's
      // never carried over from a previous slip's session.
      setForceReprint(false);
      setDetailDialogOpen(true);
    } catch (error) {
      toast.error('Failed to fetch slip details');
    }
  };

  const handleStartPicking = (slipNumber) => {
    navigate(`/wms/fg-picker-guidance?slip=${slipNumber}`);
  };

  const handleUpdateDc = async () => {
    if (!dcNumber.trim()) {
      toast.error('Please enter DC number');
      return;
    }
    
    if (!selectedTransporter) {
      toast.error('Please select a transporter');
      return;
    }

    try {
      // Find the selected transporter name
      const transporter = transporters.find(t => t.transport_id === selectedTransporter);
      
      // Use query parameter endpoint to handle slashes in slip numbers
      await apiClient.put(`/wms/fg/pick-slip-dc?slip_number=${encodeURIComponent(dcSlipNumber)}`, {
        dc_number: dcNumber,
        transport_id: selectedTransporter,
        transport_name: transporter?.name || ''
      });
      toast.success('DC number updated');
      setDcDialogOpen(false);
      setDcNumber('');
      setSelectedTransporter('');
      setTransporterSearch('');
      fetchPickSlips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to update DC number');
    }
  };

  const handleUnlinkDc = async (slipNumber) => {
    if (!window.confirm(`Are you sure you want to unlink the DC number from ${slipNumber}?`)) return;
    try {
      const response = await apiClient.delete(`/wms/fg/pick-slips/${encodeURIComponent(slipNumber)}/dc`);
      toast.success(response.data.message);
      fetchPickSlips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to unlink DC number');
    }
  };

  // Fetch transporters for DC dialog
  const fetchTransporters = async () => {
    try {
      const response = await apiClient.get('/transport/active');
      setTransporters(response.data.transports || []);
    } catch (error) {
      console.error('Failed to fetch transporters:', error);
    }
  };

  // Auto transport recommendation (weight-based cost + historical TAT)
  const fetchRecommendation = async (slipNumber) => {
    setLoadingReco(true);
    try {
      const res = await apiClient.get(`/transport/recommend/${encodeURIComponent(slipNumber)}`);
      setRecommendation(res.data);
      if (res.data.recommended_transport_id) {
        setSelectedTransporter(res.data.recommended_transport_id);
        setTransporterSearch(res.data.recommended_transport_name || '');
      }
    } catch (e) {
      setRecommendation({ error: e.response?.data?.detail || 'Could not compute a recommendation' });
    } finally {
      setLoadingReco(false);
    }
  };

  // Open DC dialog with transporter fetch + auto recommendation
  const openDcDialog = async (slipNumber, existingDcNumber = '') => {
    setDcSlipNumber(slipNumber);
    setDcNumber(existingDcNumber); // Pre-fill with existing DC if editing
    setSelectedTransporter('');
    setTransporterSearch('');
    setRecommendation(null);
    setDcDialogOpen(true);
    await fetchTransporters();
    fetchRecommendation(slipNumber);
  };

  // Filter transporters based on search
  const filteredTransporters = transporters.filter(t => 
    t.name?.toLowerCase().includes(transporterSearch.toLowerCase()) ||
    t.transport_id?.toLowerCase().includes(transporterSearch.toLowerCase())
  );

  const handleExport = async () => {
    if (!exportStartDate || !exportEndDate) {
      toast.error('Please select date range');
      return;
    }

    try {
      setExportLoading(true);
      
      // First get export info to know how many pages
      const infoResponse = await apiClient.get(
        `/wms/fg/pick-slips/export-info?start_date=${exportStartDate}T00:00:00Z&end_date=${exportEndDate}T23:59:59Z`
      );
      
      const { total_rows, total_pages } = infoResponse.data;
      
      if (total_rows === 0) {
        toast.error('No data found for the selected date range');
        setExportLoading(false);
        return;
      }
      
      setExportInfo(infoResponse.data);
      
      // If single page, download directly
      if (total_pages === 1) {
        await downloadExportPage(1);
        toast.success(`Export downloaded (${total_rows} rows)`);
        setExportDialogOpen(false);
      } else {
        toast.info(`Data split into ${total_pages} files (${total_rows.toLocaleString()} total rows). Click to download each file.`);
      }
    } catch (error) {
      toast.error('Failed to prepare export');
    } finally {
      setExportLoading(false);
    }
  };

  const downloadExportPage = async (page) => {
    try {
      const response = await apiClient.get(
        `/wms/fg/pick-slips/export?start_date=${exportStartDate}T00:00:00Z&end_date=${exportEndDate}T23:59:59Z&page=${page}`,
        { responseType: 'blob' }
      );
      
      const totalPages = response.headers['x-total-pages'] || '1';
      const filename = totalPages > 1 
        ? `fg_picked_data_${exportStartDate}_to_${exportEndDate}_page${page}of${totalPages}.csv`
        : `fg_picked_data_${exportStartDate}_to_${exportEndDate}.csv`;
      
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      
      toast.success(`Downloaded page ${page} of ${totalPages}`);
    } catch (error) {
      toast.error(`Failed to download page ${page}`);
    }
  };

  const handlePrintSummary = async (slipNumber) => {
    try {
      // Open PDF in new window for printing
      const baseUrl = apiClient.defaults.baseURL || '';
      const token = localStorage.getItem('session_token');
      const url = `${baseUrl}/wms/fg/pick-slips/print-summary/${encodeURIComponent(slipNumber)}`;
      
      // Fetch with auth and open
      const response = await apiClient.get(
        `/wms/fg/pick-slips/print-summary/${encodeURIComponent(slipNumber)}`,
        { responseType: 'blob' }
      );
      
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = window.URL.createObjectURL(pdfBlob);
      
      // Open in new window for printing
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => {
          printWindow.print();
        };
      }
      
      toast.success('Pick slip summary opened for printing');
    } catch (error) {
      toast.error('Failed to generate print summary');
    }
  };

  // Batch-print ALL carton box slips for a slip (after DC/transport tagging).
  const handlePrintBoxSlips = async (slipNumber) => {
    try {
      const response = await apiClient.get(
        `/wms/packing/slip/${slipNumber}/box-slips-pdf`,
        { responseType: 'blob' }
      );
      const pdfBlob = new Blob([response.data], { type: 'application/pdf' });
      const pdfUrl = window.URL.createObjectURL(pdfBlob);
      const printWindow = window.open(pdfUrl, '_blank');
      if (printWindow) {
        printWindow.onload = () => printWindow.print();
      }
      toast.success('All box slips opened for printing');
    } catch (error) {
      const msg = error.response?.status === 404
        ? 'No cartons packed for this slip yet'
        : (error.response?.data?.detail || 'Failed to generate box slips');
      toast.error(typeof msg === 'string' ? msg : 'Failed to generate box slips');
    }
  };

  // Admin — open the "add piece to a closed carton" dialog for a slip
  const openAddToCartonDialog = async (slip) => {
    setAddBoxSlip(slip);
    setAddBoxUsn('');
    setAddBoxSelectedId('');
    setAddBoxList([]);
    setAddBoxDialogOpen(true);
    setAddBoxLoading(true);
    try {
      const res = await apiClient.get(`/wms/packing/boxes/${encodeURIComponent(slip.pick_slip_number)}`);
      const boxes = res.data?.boxes || [];
      setAddBoxList(boxes);
      if (boxes.length === 1) setAddBoxSelectedId(boxes[0].box_id);
    } catch (e) {
      toast.error('Failed to load cartons for this slip');
    } finally {
      setAddBoxLoading(false);
    }
  };

  const refreshAddBoxList = async () => {    if (!addBoxSlip) return;
    try {
      const res = await apiClient.get(`/wms/packing/boxes/${encodeURIComponent(addBoxSlip.pick_slip_number)}`);
      setAddBoxList(res.data?.boxes || []);
    } catch (e) { /* noop */ }
  };

  const handleAddPieceToBox = async () => {
    const usn = (addBoxUsn || '').trim();
    if (!addBoxSelectedId) { toast.error('Select a carton first'); return; }
    if (!usn) { toast.error('Scan a USN'); return; }
    setAddBoxBusy(true);
    try {
      const res = await apiClient.post('/wms/packing/box/add-piece', { box_id: addBoxSelectedId, usn_code: usn });
      toast.success(res.data?.message || `Added ${usn}`);
      setAddBoxUsn('');
      await refreshAddBoxList();
      fetchPickSlips();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Could not add the piece');
    } finally {
      setAddBoxBusy(false);
    }
  };

  const handleDeleteBox = async (boxId) => {
    setDeletingBoxId(boxId);
    try {
      const res = await apiClient.delete(`/wms/packing/box/${boxId}`);
      toast.success(res.data?.message || 'Carton deleted');
      setConfirmDeleteBoxId(null);
      if (addBoxSelectedId === boxId) setAddBoxSelectedId('');
      await refreshAddBoxList();
      fetchPickSlips();
    } catch (e) {
      toast.error(e.response?.data?.detail || 'Failed to delete carton');
    } finally {
      setDeletingBoxId(null);
    }
  };

  // Open cancel dialog
  const openCancelDialog = (slipNumber, slipStatus = '') => {
    setCancelSlipNumber(slipNumber);
    setCancelSlipStatus(slipStatus);
    setCancellationReason('');
    setForceCancel(false);
    setCancelDialogOpen(true);
  };

  // Handle cancel pick slip
  const handleCancelPickSlip = async () => {
    if (!cancellationReason.trim()) {
      toast.error('Cancellation reason is mandatory');
      return;
    }

    try {
      setCancelSubmitting(true);
      const response = await apiClient.post('/wms/fg/pick-slips/cancel', {
        slip_number: cancelSlipNumber,
        cancellation_reason: cancellationReason.trim(),
        force_cancel: forceCancel
      });
      
      // Close cancel dialog
      setCancelDialogOpen(false);
      
      // Store cancelled slip info for success dialog
      setCancelledSlipInfo({
        slipNumber: cancelSlipNumber,
        pendingPutAwayCount: response.data.pending_put_away_count || 0,
        cancelledBy: response.data.cancelled_by,
        note: response.data.note,
        forceCancel: response.data.force_cancel,
        unlinkedDC: response.data.unlinked_dc,
        previousStatus: response.data.previous_status
      });
      
      // Open success confirmation dialog
      setSuccessDialogOpen(true);
      
      // Refresh list
      fetchPickSlips();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel pick slip');
    } finally {
      setCancelSubmitting(false);
    }
  };

  // Handle pushing cancelled slip items to put-away queue
  const handlePushToQueue = async (slipNumber) => {
    try {
      setPushingToQueue(slipNumber);
      const response = await apiClient.post(`/wms/fg/pick-slips/push-to-putaway-queue?slip_number=${encodeURIComponent(slipNumber)}`);
      
      if (response.data.success) {
        if (response.data.pending_put_away_count > 0) {
          toast.success(`${response.data.pending_put_away_count} item(s) pushed to Put-Away Queue!`);
        } else if (response.data.already_in_queue > 0) {
          toast.info(`Items already in Put-Away Queue (${response.data.already_in_queue})`);
        } else {
          toast.info('No items found to push to queue');
        }
        // Refresh to show updated status
        fetchPickSlips();
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to push to queue');
    } finally {
      setPushingToQueue(null);
    }
  };

  // Handle repair statuses (fix slips where items are COMPLETED but slip status is wrong)
  const handleRepairStatuses = async () => {
    try {
      setRepairLoading(true);
      const response = await apiClient.post('/wms/fg/pick-slips/repair-statuses');
      setRepairResult(response.data);
      setRepairDialogOpen(true);
      
      // Refresh list to show updated statuses
      fetchPickSlips();
      
      if (response.data.fixed_count > 0) {
        toast.success(`Fixed ${response.data.fixed_count} pick slip(s)!`);
      } else {
        toast.info('No pick slips needed repair');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to repair statuses');
    } finally {
      setRepairLoading(false);
    }
  };

  // Handle rollback of incorrectly auto-fixed statuses
  const handleRollback = async () => {
    if (!window.confirm('This will rollback incorrectly fixed slips back to IMPORTED. Continue?')) {
      return;
    }
    
    try {
      setRollbackLoading(true);
      const response = await apiClient.post('/wms/fg/pick-slips/rollback-auto-fix');
      
      if (response.data.reverted_count > 0) {
        toast.success(`Rolled back ${response.data.reverted_count} slip(s) to IMPORTED`);
        fetchPickSlips();
      } else {
        toast.info('No slips needed rollback');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to rollback');
    } finally {
      setRollbackLoading(false);
    }
  };

  // Open reopen dialog
  const openReopenDialog = (slipNumber) => {
    setReopenSlipNumber(slipNumber);
    setReopenReason('');
    setReopenDialogOpen(true);
  };

  // Handle reopen pick slip
  const handleReopenPickSlip = async () => {
    try {
      setReopenSubmitting(true);
      const response = await apiClient.post('/wms/fg/pick-slips/reopen', {
        slip_number: reopenSlipNumber,
        reason: reopenReason.trim() || undefined
      });
      
      toast.success(response.data.message || 'Pick slip reopened successfully');
      setReopenDialogOpen(false);
      fetchPickSlips();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reopen pick slip');
    } finally {
      setReopenSubmitting(false);
    }
  };

  // 2026-05-14 — Open the Re-arm Pack Prints dialog (Admin only).
  const openReArmDialog = (slipNumber) => {
    setReArmSlipNumber(slipNumber);
    setReArmReason('');
    setReArmResult(null);
    setReArmDialogOpen(true);
  };

  // 2026-05-14 — Execute Re-arm Pack Prints. Clears `restickered_at` per
  // pack via the admin endpoint; pick slip status is untouched.
  const handleReArmPackPrints = async () => {
    try {
      setReArmSubmitting(true);
      const encodedSlip = encodeURIComponent(reArmSlipNumber);
      const response = await apiClient.post(
        `/wms/fg/pick-slips/${encodedSlip}/rearm-pack-prints`,
        { reason: reArmReason.trim() || undefined },
      );
      setReArmResult(response.data);
      const msg = response.data?.message || `Re-armed ${response.data?.re_armed || 0} pack(s).`;
      if ((response.data?.re_armed || 0) > 0) toast.success(msg);
      else toast(msg);
      fetchPickSlips();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to re-arm pack prints');
    } finally {
      setReArmSubmitting(false);
    }
  };


  // Fetch staff list for reassignment
  const fetchStaffList = async () => {
    try {
      const response = await apiClient.get('/wms/dispatch/staff-list');
      setStaffList(response.data.staff || []);
    } catch (error) {
      console.error('Failed to fetch staff list:', error);
      toast.error('Failed to load staff list');
    }
  };

  // Open reassign dialog
  const openReassignDialog = (slip) => {
    setReassignSlipNumber(slip.pick_slip_number);
    setReassignSlipInfo({
      currentPicker: slip.picker_name || slip.assigned_picker_name || 'Unassigned',
      currentPickerId: slip.assigned_picker || '',
      status: slip.status,
      totalQty: slip.total_qty || 0,
      totalItems: slip.total_items || 0
    });
    setReassignReason('');
    setSelectedNewPicker('');
    setStaffSearchTerm('');
    setReassignDialogOpen(true);
    fetchStaffList();
  };

  // Handle reassign pick slip
  const handleReassignPickSlip = async () => {
    if (!selectedNewPicker) {
      toast.error('Please select a new picker');
      return;
    }

    try {
      setReassignSubmitting(true);
      const response = await apiClient.post('/wms/fg/pick-slips/reassign', {
        slip_number: reassignSlipNumber,
        new_picker_staff_id: selectedNewPicker,
        reason: reassignReason.trim() || undefined
      });
      
      toast.success(response.data.message || 'Pick slip reassigned successfully');
      setReassignDialogOpen(false);
      fetchPickSlips();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reassign pick slip');
    } finally {
      setReassignSubmitting(false);
    }
  };

  // View reassignment history
  const viewReassignmentHistory = async (slipNumber) => {
    try {
      const response = await apiClient.get(`/wms/fg/pick-slips/${encodeURIComponent(slipNumber)}/reassignment-history`);
      setReassignmentHistory(response.data);
      setHistoryDialogOpen(true);
    } catch (error) {
      toast.error('Failed to load reassignment history');
    }
  };

  // Filter staff list based on search
  const filteredStaffList = staffList.filter(staff => 
    staff.staff_name?.toLowerCase().includes(staffSearchTerm.toLowerCase()) ||
    staff.staff_id?.toLowerCase().includes(staffSearchTerm.toLowerCase())
  );

  // Open Cancel USN dialog
  const openCancelUSNDialog = async (slipNumber) => {
    setCancelUSNSlipNumber(slipNumber);
    setCancelUSNDialogOpen(true);
    setSelectedUSNsToCancel([]);
    setCancelUSNReason('');
    setCancelUSNCustomReason('');
    setUsnSearchTerm('');
    setLoadingUSNs(true);
    
    try {
      const response = await apiClient.get(`/wms/fg/pick-slips/${encodeURIComponent(slipNumber)}/picked-usns`);
      setPickedUSNs(response.data.picked_usns || []);
      setUsnPickPack(!!response.data.pick_pack_combined);
      setCancelledUSNs(response.data.cancelled_usns || []);
    } catch (error) {
      toast.error('Failed to load picked USNs');
      setPickedUSNs([]);
      setCancelledUSNs([]);
    } finally {
      setLoadingUSNs(false);
    }
  };

  // Toggle USN selection
  const toggleUSNSelection = (usn) => {
    setSelectedUSNsToCancel(prev => {
      const isSelected = prev.some(u => u.usn_code === usn.usn_code);
      if (isSelected) {
        return prev.filter(u => u.usn_code !== usn.usn_code);
      } else {
        return [...prev, usn];
      }
    });
  };

  // Select all filtered USNs
  const selectAllUSNs = () => {
    const filtered = filteredPickedUSNs;
    setSelectedUSNsToCancel(filtered);
  };

  // Deselect all
  const deselectAllUSNs = () => {
    setSelectedUSNsToCancel([]);
  };

  // Filter picked USNs by search term
  const filteredPickedUSNs = pickedUSNs.filter(usn => 
    usn.usn_code?.toLowerCase().includes(usnSearchTerm.toLowerCase()) ||
    usn.item_barcode?.toLowerCase().includes(usnSearchTerm.toLowerCase()) ||
    usn.design_name?.toLowerCase().includes(usnSearchTerm.toLowerCase())
  );

  // Handle cancel USN(s) - supports both single and bulk
  const handleCancelUSN = async () => {
    if (selectedUSNsToCancel.length === 0) {
      toast.error('Please select at least one USN to cancel');
      return;
    }
    
    const reason = cancelUSNReason === 'Other' ? cancelUSNCustomReason : cancelUSNReason;
    if (!reason.trim()) {
      toast.error('Please provide a cancellation reason');
      return;
    }
    
    setCancelUSNSubmitting(true);
    try {
      let response;
      
      if (selectedUSNsToCancel.length === 1) {
        // Single USN cancellation
        response = await apiClient.post('/wms/fg/pick-slips/cancel-usn', {
          slip_number: cancelUSNSlipNumber,
          usn_code: selectedUSNsToCancel[0].usn_code,
          cancellation_reason: reason
        });
      } else {
        // Bulk USN cancellation
        response = await apiClient.post('/wms/fg/pick-slips/cancel-usns-bulk', {
          slip_number: cancelUSNSlipNumber,
          usn_codes: selectedUSNsToCancel.map(u => u.usn_code),
          cancellation_reason: reason
        });
      }
      
      toast.success(response.data.message || 'USN(s) cancelled successfully');
      
      // Show failed USNs if any
      if (response.data.failed_usns && response.data.failed_usns.length > 0) {
        toast.warning(`${response.data.failed_usns.length} USN(s) failed to cancel`);
      }
      
      // Refresh the USN list
      const refreshResponse = await apiClient.get(`/wms/fg/pick-slips/${encodeURIComponent(cancelUSNSlipNumber)}/picked-usns`);
      setPickedUSNs(refreshResponse.data.picked_usns || []);
      setCancelledUSNs(refreshResponse.data.cancelled_usns || []);
      
      // Reset selection
      setSelectedUSNsToCancel([]);
      setCancelUSNReason('');
      setCancelUSNCustomReason('');
      
      // Refresh pick slips list
      fetchPickSlips();
      
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to cancel USN(s)');
    } finally {
      setCancelUSNSubmitting(false);
    }
  };

  const getStatusBadge = (status) => {
    const config = {
      IMPORTED: { color: 'bg-gray-100 text-gray-700', label: 'Imported' },
      ASSIGNED: { color: 'bg-cyan-100 text-cyan-700', label: 'Assigned' },
      IN_PROGRESS: { color: 'bg-blue-100 text-blue-700', label: 'In Progress' },
      COMPLETED: { color: 'bg-green-100 text-green-700', label: 'Completed' },
      COMPLETED_WITH_SKIPS: { color: 'bg-orange-100 text-orange-700', label: 'Completed (Skips)' },
      PACKED: { color: 'bg-emerald-100 text-emerald-700', label: 'Packed' },
      PARTIAL: { color: 'bg-yellow-100 text-yellow-700', label: 'Partial' },
      DC_LINKED: { color: 'bg-purple-100 text-purple-700', label: 'DC Linked' },
      CANCELLED: { color: 'bg-red-100 text-red-700', label: 'Cancelled' }
    };
    const { color, label } = config[status] || config.IMPORTED;
    return <Badge className={`${color} font-medium`}>{label}</Badge>;
  };

  const filteredSlips = pickSlips.filter(slip =>
    slip.pick_slip_number.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#FFFBF5]">
      <header className="bg-[#1A1F2E] text-[#F9FAFB] shadow-lg">
        <div className="max-w-7xl mx-auto px-4 md:px-6 h-16 flex items-center justify-between">
          <div className="flex items-center">
            <Button
              variant="ghost"
              onClick={() => navigate('/wms')}
              className="text-[#F9FAFB] hover:bg-white/10 mr-4"
            >
              <ArrowLeft className="w-5 h-5" />
            </Button>
            <div>
              <h1 className="text-xl font-bold font-display">FG Pick Slips</h1>
              <p className="text-sm text-[#E8DCC8]">Pick slips from accounting software</p>
            </div>
          </div>
          <div className="flex gap-2">
            <Button
              variant="outline"
              onClick={() => navigate('/wms/fg/packed-history')}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="packed-history-btn"
            >
              <History className="w-4 h-4 mr-2" />
              Packed History
            </Button>
            <Button
              variant="outline"
              onClick={() => navigate('/admin/import')}
              className="border-white/20 text-white hover:bg-white/10"
            >
              <Upload className="w-4 h-4 mr-2" />
              Import Slips
            </Button>
            <Button
              variant="outline"
              onClick={() => setShowErpSettings(true)}
              className="border-white/20 text-white hover:bg-white/10"
              data-testid="erp-stockpoints-btn"
            >
              <Settings2 className="w-4 h-4 mr-2" />
              ERP Stock Points
            </Button>
            {isAdmin && (
              <Button
                variant="outline"
                onClick={openReconcile}
                className="border-white/20 text-white hover:bg-white/10"
                data-testid="reconcile-picks-btn"
              >
                <RotateCcw className="w-4 h-4 mr-2" />
                Fix Stuck Picks
              </Button>
            )}
            <Button
              onClick={() => setExportDialogOpen(true)}
              className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
            >
              <Download className="w-4 h-4 mr-2" />
              Export Picked
            </Button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-4 md:px-6 py-8">
        {/* Summary Stats Tiles */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">Total Pick Slip Qty</p>
                <p className="text-2xl font-bold text-[#1A1F2E]" data-testid="total-qty-stat">
                  {summaryStats.total_qty?.toLocaleString() || 0}
                </p>
              </div>
              <Package className="w-8 h-8 text-blue-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">In Progress Qty</p>
                <p className="text-2xl font-bold text-[#1A1F2E]" data-testid="in-progress-qty-stat">
                  {summaryStats.in_progress_qty?.toLocaleString() || 0}
                </p>
              </div>
              <TrendingUp className="w-8 h-8 text-amber-500" />
            </div>
          </div>
          <div className="bg-white rounded-lg border border-[#E5DFD0] p-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm text-[#5A5F6E]">Pending Qty</p>
                <p className="text-2xl font-bold text-[#1A1F2E]" data-testid="pending-qty-stat">
                  {summaryStats.pending_qty?.toLocaleString() || 0}
                </p>
              </div>
              <Clock className="w-8 h-8 text-red-500" />
            </div>
          </div>
        </div>

        {/* Filters */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] p-4 mb-6">
          <div className="flex flex-wrap gap-4 items-center">
            <div className="flex items-center gap-2">
              <Filter className="w-4 h-4 text-[#5A5F6E]" />
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-40" data-testid="status-filter">
                  <SelectValue placeholder="Filter by status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="IMPORTED">Imported</SelectItem>
                  <SelectItem value="ASSIGNED">Assigned</SelectItem>
                  <SelectItem value="IN_PROGRESS">In Progress</SelectItem>
                  <SelectItem value="COMPLETED">Completed</SelectItem>
                  <SelectItem value="COMPLETED_WITH_SKIPS">Completed (Skips)</SelectItem>
                  <SelectItem value="PARTIAL">Partial</SelectItem>
                  <SelectItem value="DC_LINKED">DC Linked</SelectItem>
                  <SelectItem value="PACKED">Packed</SelectItem>
                  <SelectItem value="CANCELLED">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            {/* Date Filter */}
            <div className="flex items-center gap-2">
              <Calendar className="w-4 h-4 text-[#5A5F6E]" />
              <Input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                className="w-36"
                placeholder="From"
                data-testid="start-date-filter"
              />
              <span className="text-[#5A5F6E]">to</span>
              <Input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                className="w-36"
                placeholder="To"
                data-testid="end-date-filter"
              />
              {(startDate || endDate) && (
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={() => { setStartDate(''); setEndDate(''); }}
                  className="text-gray-500 hover:text-gray-700"
                >
                  Clear
                </Button>
              )}
            </div>
            <div className="flex-1 min-w-[200px]">
              <Input
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                placeholder="Search by slip number"
                className="max-w-md"
                data-testid="slip-search-input"
              />
            </div>
            <div className="text-sm text-[#5A5F6E]">
              {filteredSlips.length} slip(s)
            </div>
          </div>
        </div>

        {/* Pick Slips Table */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden">
          {loading ? (
            <div className="p-8 text-center text-[#5A5F6E]">Loading...</div>
          ) : filteredSlips.length === 0 ? (
            <div className="p-12 text-center">
              <FileSpreadsheet className="w-12 h-12 text-[#E5DFD0] mx-auto mb-4" />
              <p className="text-[#5A5F6E]">No pick slips found.</p>
              <Button
                variant="link"
                onClick={() => navigate('/admin/import')}
                className="text-[#2D5F4F] mt-2"
              >
                Import pick slips from accounting →
              </Button>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full text-sm">
                <thead className="bg-gray-50 border-b border-[#E5DFD0]">
                  <tr>
                    <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Slip Number</th>
                    <th className="px-4 py-3 text-center font-medium text-[#1A1F2E]">Items</th>
                    <th className="px-4 py-3 text-right font-medium text-[#1A1F2E]">Total Qty</th>
                    <th className="px-4 py-3 text-center font-medium text-[#1A1F2E]">Status</th>
                    <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Assigned To</th>
                    <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">DC Number</th>
                    <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Transporter</th>
                    <th className="px-4 py-3 text-left font-medium text-[#1A1F2E]">Imported</th>
                    <th className="px-4 py-3 text-center font-medium text-[#1A1F2E]">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {filteredSlips.map((slip) => (
                    <tr key={slip.slip_id} className="border-b border-[#E5DFD0] hover:bg-gray-50">
                      <td className="px-4 py-3 font-mono font-medium text-[#1A1F2E]">
                        <div className="flex items-center gap-2">
                          <span>{slip.pick_slip_number}</span>
                          {slip.is_seasonal && (
                            <span
                              data-testid={`slip-seasonal-badge-${slip.pick_slip_number}`}
                              className="inline-flex items-center gap-1 rounded-full bg-lime-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-lime-800 border border-lime-300"
                              title="Seasonal pick slip (packed ahead). Assigned to FG pickers only after all regular slips (FIFO)."
                            >
                              <PackageCheck className="w-3 h-3" />
                              Seasonal
                            </span>
                          )}
                          {slip.has_rate_changed_skus && (
                            <span
                              data-testid={`slip-rate-changed-badge-${slip.pick_slip_number}`}
                              className="inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold uppercase tracking-wide text-amber-800 border border-amber-300"
                              title="One or more SKUs on this slip have a pending price revision. Open detail to see which."
                            >
                              <AlertTriangle className="w-3 h-3" />
                              Rate Changed
                            </span>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-center">{slip.total_items}</td>
                      <td className="px-4 py-3 text-right font-medium">{slip.total_qty?.toLocaleString()}</td>
                      <td className="px-4 py-3 text-center">{getStatusBadge(slip.status)}</td>
                      <td className="px-4 py-3">
                        <div className="flex items-center gap-1">
                          <span className={`text-sm ${slip.picker_name || slip.assigned_picker_name ? 'text-[#1A1F2E]' : 'text-gray-400 italic'}`}>
                            {slip.picker_name || slip.assigned_picker_name || 'Unassigned'}
                          </span>
                          {slip.reassignment_history?.length > 0 && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => viewReassignmentHistory(slip.pick_slip_number)}
                              className="text-gray-400 hover:text-gray-600 p-0 h-auto"
                              title="View reassignment history"
                            >
                              <History className="w-3 h-3" />
                            </Button>
                          )}
                        </div>
                      </td>
                      <td className="px-4 py-3 text-[#5A5F6E]">
                        {slip.dc_number || '-'}
                      </td>
                      <td className="px-4 py-3 text-[#5A5F6E]">
                        {slip.transport_name || '-'}
                      </td>
                      <td className="px-4 py-3 text-[#5A5F6E] text-sm">
                        {new Date(slip.import_date).toLocaleDateString()}
                      </td>
                      <td className="px-4 py-3">
                        <div className="flex items-center justify-center gap-1">
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => handleViewDetail(slip.pick_slip_number)}
                            className="text-blue-600"
                            title="View Details"
                            data-testid={`view-detail-${slip.slip_id}`}
                          >
                            <Eye className="w-4 h-4" />
                          </Button>
                          {isAdmin && (slip.status === 'IMPORTED' || slip.status === 'ASSIGNED' || slip.status === 'IN_PROGRESS') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleStartPicking(slip.pick_slip_number)}
                              className="text-green-600"
                              title="Start Picking"
                              data-testid={`start-picking-${slip.slip_id}`}
                            >
                              <Play className="w-4 h-4" />
                            </Button>
                          )}
                          {(slip.status === 'COMPLETED' || slip.status === 'COMPLETED_WITH_SKIPS' || slip.status === 'PARTIAL') && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handlePrintSummary(slip.pick_slip_number)}
                              className="text-orange-600"
                              title="Print Summary for Packing"
                              data-testid={`print-summary-${slip.slip_id}`}
                            >
                              <Printer className="w-4 h-4" />
                            </Button>
                          )}
                          {(slip.status === 'COMPLETED' || slip.status === 'COMPLETED_WITH_SKIPS' || slip.status === 'PARTIAL' || slip.status === 'PACKED') && !slip.dc_number && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openDcDialog(slip.pick_slip_number)}
                              className="text-purple-600"
                              title="Add / Re-link DC Number"
                              data-testid={`update-dc-${slip.slip_id}`}
                            >
                              <FileCheck className="w-4 h-4" />
                            </Button>
                          )}
                          {(slip.status === 'COMPLETED' || slip.status === 'COMPLETED_WITH_SKIPS' || slip.status === 'PARTIAL' || slip.status === 'PACKED') && !slip.dc_number && !slip.is_seasonal && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => setErpSlip(slip)}
                              className="text-emerald-600"
                              title="Convert to DC in ERP"
                              data-testid={`erp-convert-${slip.slip_id}`}
                            >
                              <Send className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Print all carton box slips — after packing (PACKED / DC-linked) */}
                          {['PACKED', 'DC_LINKED'].includes(slip.status) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handlePrintBoxSlips(slip.pick_slip_number)}
                              className="text-fuchsia-600 hover:text-fuchsia-700 hover:bg-fuchsia-50"
                              title="Print all carton box slips"
                              data-testid={`print-box-slips-${slip.slip_id}`}
                            >
                              <Printer className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Add / delete a carton (admin, supervisor, incharge) — not for DC-linked/dispatched */}
                          {canManageCarton && ['PACKED', 'PARTIAL', 'COMPLETED', 'COMPLETED_WITH_SKIPS'].includes(slip.status) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openAddToCartonDialog(slip)}
                              className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                              title="Add a piece to / delete a carton"
                              data-testid={`add-to-carton-${slip.slip_id}`}
                            >
                              <Package className="w-4 h-4" />
                            </Button>
                          )}
                          {slip.status === 'DC_LINKED' && slip.dc_number && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openDcDialog(slip.pick_slip_number, slip.dc_number)}
                              className="text-blue-600"
                              title="Edit DC Number"
                              data-testid={`edit-dc-${slip.slip_id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Unlink DC Number - Admin only, for DC_LINKED slips */}
                          {isAdmin && slip.status === 'DC_LINKED' && slip.dc_number && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handleUnlinkDc(slip.pick_slip_number)}
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                              title="Unlink DC Number"
                              data-testid={`unlink-dc-${slip.slip_id}`}
                            >
                              <LinkIcon className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Reopen button - Admin only, for COMPLETED/PARTIAL slips without DC */}
                          {isAdmin && (slip.status === 'COMPLETED' || slip.status === 'COMPLETED_WITH_SKIPS' || slip.status === 'PARTIAL') && !slip.dc_number && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openReopenDialog(slip.pick_slip_number)}
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              title="Reopen Pick Slip (Edit)"
                              data-testid={`reopen-slip-${slip.slip_id}`}
                            >
                              <Edit className="w-4 h-4" />
                            </Button>
                          )}

                          {/* 2026-05-14 — Re-arm Pack Prints (Admin only).
                              Clears `restickered_at` per pack so the Print
                              Sticker buttons work again for a half-printed
                              re-sticker batch. Does NOT change slip status
                              or roll back USN replacements. Visible for
                              any non-CANCELLED slip — covers slips that
                              are still IN_PROGRESS as well as COMPLETED /
                              PARTIAL / DC_LINKED / PACKED. */}
                          {isAdmin && slip.status !== 'CANCELLED' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openReArmDialog(slip.pick_slip_number)}
                              className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                              title="Re-arm Pack Prints — clears restickered_at flag so the Print Sticker button works again for half-printed batches"
                              data-testid={`rearm-prints-slip-${slip.slip_id}`}
                            >
                              <RotateCcw className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Cancel button - only for authorized roles and non-completed slips */}
                          {/* Admin can cancel DC_LINKED and PACKED slips using force cancel */}
                          {canCancel && slip.status !== 'CANCELLED' && (
                            (slip.status !== 'DC_LINKED' && slip.status !== 'PACKED') || isAdmin
                          ) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openCancelDialog(slip.pick_slip_number, slip.status)}
                              className={`hover:bg-red-50 ${
                                ['DC_LINKED', 'PACKED'].includes(slip.status) 
                                  ? 'text-orange-600 hover:text-orange-700' 
                                  : 'text-red-600 hover:text-red-700'
                              }`}
                              title={['DC_LINKED', 'PACKED'].includes(slip.status) 
                                ? 'Force Cancel (Admin Only)' 
                                : 'Cancel Pick Slip'
                              }
                              data-testid={`cancel-slip-${slip.slip_id}`}
                            >
                              <XCircle className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Reassign button - for authorized roles, IMPORTED/ASSIGNED/IN_PROGRESS slips */}
                          {canReassign && ['IMPORTED', 'ASSIGNED', 'IN_PROGRESS'].includes(slip.status) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openReassignDialog(slip)}
                              className="text-indigo-600 hover:text-indigo-700 hover:bg-indigo-50"
                              title="Reassign to another picker"
                              data-testid={`reassign-slip-${slip.slip_id}`}
                            >
                              <UserCheck className="w-4 h-4" />
                            </Button>
                          )}
                          {/* View/Cancel Picked USNs - for COMPLETED/PARTIAL slips (+ PACKED Pick & Pack slips) */}
                          {canCancel && (['COMPLETED', 'COMPLETED_WITH_SKIPS', 'PARTIAL'].includes(slip.status) || (slip.status === 'PACKED' && slip.pick_pack_combined)) && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => openCancelUSNDialog(slip.pick_slip_number)}
                              className="text-amber-600 hover:text-amber-700 hover:bg-amber-50"
                              title="View/Cancel Picked USNs"
                              data-testid={`view-usns-${slip.slip_id}`}
                            >
                              <List className="w-4 h-4" />
                            </Button>
                          )}
                          {/* Push to Put-Away Queue - only for cancelled slips */}
                          {canCancel && slip.status === 'CANCELLED' && (
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => handlePushToQueue(slip.pick_slip_number)}
                              disabled={pushingToQueue === slip.pick_slip_number}
                              className="text-orange-600 hover:text-orange-700 hover:bg-orange-50"
                              title="Push to Put-Away Queue"
                              data-testid={`push-queue-${slip.slip_id}`}
                            >
                              <RotateCcw className={`w-4 h-4 ${pushingToQueue === slip.pick_slip_number ? 'animate-spin' : ''}`} />
                            </Button>
                          )}
                        </div>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>

        {/* Design Transfer Slips Section */}
        <div className="bg-white rounded-lg border border-[#E5DFD0] overflow-hidden mt-6">
          <div className="bg-gradient-to-r from-purple-50 to-purple-100 px-4 py-3 border-b border-purple-200 flex justify-between items-center">
            <div className="flex items-center gap-2">
              <FileSpreadsheet className="w-5 h-5 text-purple-600" />
              <h2 className="font-semibold text-purple-800">Design Transfer Slips</h2>
              <Badge variant="outline" className="border-purple-300 text-purple-700 ml-2">
                {designTransferSlips.length} slip(s)
              </Badge>
            </div>
            <Button
              variant="outline"
              size="sm"
              onClick={() => navigate('/wms/fg/design-transfer-slips')}
              className="border-purple-300 text-purple-700 hover:bg-purple-50"
            >
              Manage All
            </Button>
          </div>
          
          {dtLoading ? (
            <div className="flex items-center justify-center py-8">
              <div className="animate-spin w-6 h-6 border-2 border-purple-500 border-t-transparent rounded-full" />
            </div>
          ) : designTransferSlips.length === 0 ? (
            <div className="py-8 text-center text-gray-500">
              <FileSpreadsheet className="w-10 h-10 mx-auto mb-2 opacity-50" />
              <p>No design transfer slips found</p>
            </div>
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full">
                <thead className="bg-purple-50/50 text-xs uppercase text-purple-700">
                  <tr>
                    <th className="px-4 py-3 text-left">Slip No</th>
                    <th className="px-4 py-3 text-left">Items</th>
                    <th className="px-4 py-3 text-right">Total Qty</th>
                    <th className="px-4 py-3 text-center">Status</th>
                    <th className="px-4 py-3 text-left">Assigned To</th>
                    <th className="px-4 py-3 text-left">Progress</th>
                    <th className="px-4 py-3 text-center">Actions</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-purple-100">
                  {designTransferSlips.slice(0, 10).map((slip, idx) => {
                    const totalQty = slip.total_qty || 0;
                    const transferredQty = slip.items?.reduce((sum, item) => sum + (item.transferred_qty || 0), 0) || 0;
                    const progressPercent = totalQty > 0 ? Math.round((transferredQty / totalQty) * 100) : 0;
                    
                    return (
                      <tr key={slip.slip_id || `${slip.slip_number}-${idx}`} className="hover:bg-purple-50/30">
                        <td className="px-4 py-3">
                          <span className="font-mono font-medium">{slip.slip_number}</span>
                          {slip.is_urgent && (
                            <Badge variant="destructive" className="ml-2 text-xs">Urgent</Badge>
                          )}
                        </td>
                        <td className="px-4 py-3 text-sm text-gray-600">{slip.total_items}</td>
                        <td className="px-4 py-3 text-right font-medium">{totalQty}</td>
                        <td className="px-4 py-3 text-center">{getStatusBadge(slip.status)}</td>
                        <td className="px-4 py-3 text-sm">
                          {slip.assigned_to_name || <span className="text-gray-400 italic">Unassigned</span>}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex items-center gap-2">
                            <div className="w-20 bg-purple-100 rounded-full h-2">
                              <div 
                                className="bg-purple-600 h-2 rounded-full" 
                                style={{ width: `${progressPercent}%` }}
                              />
                            </div>
                            <span className="text-xs text-purple-600">{progressPercent}%</span>
                          </div>
                        </td>
                        <td className="px-4 py-3 text-center">
                          <div className="flex justify-center gap-1">
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => navigate(`/wms/fg/design-transfer-slips`)}
                              className="text-purple-600"
                              title="View Details"
                            >
                              <Eye className="w-4 h-4" />
                            </Button>
                            {['IMPORTED', 'ASSIGNED', 'IN_PROGRESS'].includes(slip.status) && (
                              <Button
                                size="sm"
                                variant="ghost"
                                onClick={() => navigate(`/wms/fg/design-transfer-picker?slip=${encodeURIComponent(slip.slip_number)}`)}
                                className="text-green-600"
                                title="Start Picking"
                              >
                                <Play className="w-4 h-4" />
                              </Button>
                            )}
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
              {designTransferSlips.length > 10 && (
                <div className="px-4 py-2 bg-purple-50/50 text-center text-sm text-purple-600 border-t border-purple-100">
                  Showing 10 of {designTransferSlips.length} slips. 
                  <button 
                    onClick={() => navigate('/wms/fg/design-transfer-slips')}
                    className="ml-1 underline hover:text-purple-800"
                  >
                    View all
                  </button>
                </div>
              )}
            </div>
          )}
        </div>
      </main>

      {/* Detail Dialog */}
      <Dialog open={detailDialogOpen} onOpenChange={setDetailDialogOpen}>
        <DialogContent className="max-w-5xl max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Pick Slip Details - {selectedSlip?.pick_slip_number}</DialogTitle>
          </DialogHeader>
          {selectedSlip && (
            <div className="py-4 space-y-4">
              {/* 2026-05-18 — Force reprint toggle. When ticked, every
                  Print Sticker click below ignores `restickered_at` and
                  reprints every pack of the chosen SKU, even those that
                  were already re-stickered earlier (covers the
                  "1 printed, 9 skipped" production scenario). */}
              <div className="flex items-start gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg" data-testid="force-reprint-toggle-block">
                <input
                  id="force-reprint-toggle"
                  type="checkbox"
                  checked={forceReprint}
                  onChange={(e) => setForceReprint(e.target.checked)}
                  className="mt-0.5 h-4 w-4 cursor-pointer accent-amber-600"
                  data-testid="force-reprint-toggle"
                />
                <label htmlFor="force-reprint-toggle" className="text-xs text-amber-900 cursor-pointer leading-tight">
                  <span className="font-semibold">Force reprint all</span>
                  {' — '}
                  ignore the `already re-stickered` flag. When ticked, Print Sticker reprints every picked pack of the chosen SKU (even ones previously re-stickered). Leave OFF for normal CSV-revision flow.
                </label>
              </div>
              {/* Summary */}
              <div className="grid grid-cols-3 gap-4 p-4 bg-gray-50 rounded-lg">
                <div>
                  <p className="text-xs text-[#5A5F6E]">Status</p>
                  {getStatusBadge(selectedSlip.status)}
                </div>
                <div>
                  <p className="text-xs text-[#5A5F6E]">Total Items</p>
                  <p className="font-medium">{selectedSlip.total_items}</p>
                </div>
                <div>
                  <p className="text-xs text-[#5A5F6E]">Total Qty</p>
                  <p className="font-medium">{selectedSlip.total_qty?.toLocaleString()}</p>
                </div>
                {selectedSlip.dc_number && (
                  <div>
                    <p className="text-xs text-[#5A5F6E]">DC Number</p>
                    <p className="font-medium">{selectedSlip.dc_number}</p>
                  </div>
                )}
                {selectedSlip.transport_name && (
                  <div>
                    <p className="text-xs text-[#5A5F6E]">Transporter</p>
                    <p className="font-medium">{selectedSlip.transport_name}</p>
                  </div>
                )}
              </div>

              {/* Items List */}
              <div>
                <div className="flex items-center justify-between mb-2">
                  <h3 className="font-medium text-[#1A1F2E]">Items ({selectedSlip.items?.length})</h3>
                  {/* 2026-05-14 — Sticker pricing-type override (state code).
                      `rates_master` rows are keyed by (barcode, pricing_type=<state>).
                      The dropdown lets the operator pick a state-specific price
                      sheet per slip when re-stickering. Defaults to TN. */}
                  <div className="flex items-center gap-2 text-xs">
                    <span className="text-[#5A5F6E]">Sticker price:</span>
                    <select
                      value={pricingType}
                      onChange={(e) => setPricingType(e.target.value)}
                      disabled={!!printingSku}
                      data-testid="pick-slip-pricing-type-select"
                      className="h-7 px-2 border border-[#E5DFD0] rounded text-xs bg-white"
                      title="State price-sheet used when resolving design / shade / size for the printed sticker"
                    >
                      {pricingChannels.map((t) => (
                        <option key={t} value={t}>{t}</option>
                      ))}
                    </select>
                  </div>
                </div>
                <div className="max-h-60 overflow-y-auto border border-[#E5DFD0] rounded-lg">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-50 sticky top-0">
                      <tr>
                        <th className="px-3 py-2 text-left">Barcode</th>
                        <th className="px-3 py-2 text-left">Design</th>
                        <th className="px-3 py-2 text-left">Shade</th>
                        <th className="px-3 py-2 text-left">Size</th>
                        <th className="px-3 py-2 text-left">Description</th>
                        <th className="px-3 py-2 text-right">Required</th>
                        <th className="px-3 py-2 text-right">Picked</th>
                        <th className="px-3 py-2 text-center">Status</th>
                        <th className="px-3 py-2 text-left">Remarks</th>
                        <th className="px-3 py-2 text-center">Re-Sticker</th>
                      </tr>
                    </thead>
                    <tbody>
                      {selectedSlip.items?.map((item, idx) => (
                        <tr key={idx} className="border-t border-[#E5DFD0]">
                          <td className="px-3 py-2 font-mono" data-testid={`slip-item-barcode-${idx}`}>
                            <div className="flex flex-col gap-1">
                              <span>{item.barcode}</span>
                              {item.has_pending_revision && (
                                <span
                                  data-testid={`slip-item-rate-changed-badge-${idx}`}
                                  className="inline-flex items-center gap-1 self-start rounded-full bg-amber-100 px-2 py-0.5 text-[10px] font-semibold text-amber-800 border border-amber-300"
                                  title={
                                    item.old_mrp
                                      ? `Old MRP ₹${item.old_mrp} → New MRP ₹${item.new_mrp}. Reprint stickers with new rate before dispatch.`
                                      : `Pending price revision. New MRP ₹${item.new_mrp}.`
                                  }
                                >
                                  <AlertTriangle className="w-3 h-3" />
                                  Rate Changed → ₹{item.new_mrp ?? '?'}
                                </span>
                              )}
                            </div>
                          </td>
                          <td className="px-3 py-2">{item.design_name || '-'}</td>
                          <td className="px-3 py-2" data-testid={`slip-item-shade-${idx}`}>{item.shade || '-'}</td>
                          <td className="px-3 py-2" data-testid={`slip-item-size-${idx}`}>{item.size || '-'}</td>
                          <td className="px-3 py-2 text-xs text-[#5A5F6E]" data-testid={`slip-item-description-${idx}`}>{item.description || '-'}</td>
                          <td className="px-3 py-2 text-right">{item.required_qty}</td>
                          <td className="px-3 py-2 text-right font-medium">
                            {item.picked_qty || 0}
                          </td>
                          <td className="px-3 py-2 text-center">
                            {item.status === 'COMPLETED' ? (
                              <CheckCircle className="w-4 h-4 text-green-600 mx-auto" />
                            ) : (
                              <Clock className="w-4 h-4 text-yellow-600 mx-auto" />
                            )}
                          </td>
                          <td className="px-3 py-2 text-[#5A5F6E] text-xs">
                            {item.picker_remarks || '-'}
                          </td>
                          <td className="px-3 py-2 text-center">
                            <div className="flex items-center gap-2 justify-center">
                              <Button
                                variant="outline"
                                size="sm"
                                disabled={
                                  !item.picked_qty
                                  || printingSku === item.barcode
                                  || (printingSku && printingSku !== item.barcode)
                                }
                                onClick={() => handlePrintStickerForSku(item.barcode)}
                                data-testid={`print-sticker-btn-${idx}`}
                                className="text-amber-700 border-amber-300 hover:bg-amber-50"
                                title={item.picked_qty
                                  ? `Reprint stickers for all ${item.picked_qty} picked USN(s) of this SKU`
                                  : 'No picked USNs yet'
                                }
                              >
                                {printingSku === item.barcode ? (
                                  <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                                ) : (
                                  <Printer className="w-3.5 h-3.5 mr-1" />
                                )}
                                {printingSku === item.barcode ? 'Printing…' : 'Print Sticker'}
                              </Button>
                              {/* 2026-05-14 — Per-row revert. Admin clears
                                  restickered_at on every pack of this SKU
                                  on this slip — rest of the slip is
                                  untouched (unlike the legacy "Revert Done"
                                  which dumped the whole slip). */}
                              {isAdmin && (
                                <Button
                                  variant="outline"
                                  size="sm"
                                  disabled={revertingSku === item.barcode || !!printingSku}
                                  onClick={() => handleRevertPickSlipRow(item.barcode)}
                                  data-testid={`revert-row-btn-${idx}`}
                                  className="text-rose-700 border-rose-300 hover:bg-rose-50"
                                  title="Revert re-sticker status for this SKU row only"
                                >
                                  {revertingSku === item.barcode ? (
                                    <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                                  ) : (
                                    <RotateCcw className="w-3.5 h-3.5 mr-1" />
                                  )}
                                  {revertingSku === item.barcode ? 'Reverting…' : 'Revert Row'}
                                </Button>
                              )}
                            </div>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
                {printingSku && (
                  <div
                    className="mt-2 p-2 bg-amber-50 border border-amber-200 rounded text-xs text-amber-800 max-h-32 overflow-y-auto"
                    data-testid="print-sticker-live-log"
                  >
                    <div className="font-semibold mb-1">Re-stickering SKU {printingSku}…</div>
                    {printLog.slice(0, 8).map((l, i) => (
                      <div key={i} className="font-mono text-[11px]">{l.msg}</div>
                    ))}
                  </div>
                )}
              </div>
            </div>
          )}
          <DialogFooter>
            <Button variant="outline" onClick={() => setDetailDialogOpen(false)}>
              Close
            </Button>
            {isAdmin && selectedSlip?.status === 'IMPORTED' && (
              <Button
                onClick={() => {
                  setDetailDialogOpen(false);
                  handleStartPicking(selectedSlip.pick_slip_number);
                }}
                className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
              >
                <Play className="w-4 h-4 mr-2" />
                Start Picking
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Print Sticker Result Dialog */}
      <Dialog open={printResultDialogOpen} onOpenChange={setPrintResultDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="print-sticker-result-dialog">
          <DialogHeader>
            <DialogTitle>
              {printResult?.success === false
                ? 'Re-Sticker Failed'
                : `Re-Sticker Complete — ${printResult?.skuCode || ''}`}
            </DialogTitle>
          </DialogHeader>
          <div className="py-3 space-y-2 text-sm">
            {printResult?.error ? (
              <div className="p-3 bg-red-50 border border-red-200 rounded text-red-800 text-xs">
                {printResult.error}
              </div>
            ) : (
              <>
                <div className="grid grid-cols-3 gap-3">
                  <div className="p-3 bg-emerald-50 rounded border border-emerald-200 text-center">
                    <div className="text-xs text-emerald-700">Printed</div>
                    <div className="text-xl font-bold text-emerald-800" data-testid="print-result-marked">
                      {printResult?.marked_count || 0}
                    </div>
                  </div>
                  <div className="p-3 bg-amber-50 rounded border border-amber-200 text-center">
                    <div className="text-xs text-amber-700">Skipped</div>
                    <div className="text-xl font-bold text-amber-800">
                      {printResult?.skipped || 0}
                    </div>
                  </div>
                  <div className="p-3 bg-rose-50 rounded border border-rose-200 text-center">
                    <div className="text-xs text-rose-700">Failed</div>
                    <div className="text-xl font-bold text-rose-800">
                      {printResult?.failed_count || 0}
                    </div>
                  </div>
                </div>
                {printResult?.replacements?.length > 0 && (
                  <div className="mt-2">
                    <div className="text-xs font-semibold text-[#5A5F6E] mb-1">
                      Replacements ({printResult.replacements.length})
                    </div>
                    <div className="max-h-40 overflow-y-auto border border-[#E5DFD0] rounded text-[11px] font-mono">
                      {printResult.replacements.slice(0, 50).map((r, i) => (
                        <div key={i} className="px-2 py-1 border-b border-[#E5DFD0] last:border-0">
                          {r.old_usn} → <span className="text-emerald-700">{r.new_usn}</span>
                        </div>
                      ))}
                      {printResult.replacements.length > 50 && (
                        <div className="px-2 py-1 text-[#5A5F6E]">…and {printResult.replacements.length - 50} more</div>
                      )}
                    </div>
                  </div>
                )}
                {printResult?.failures?.length > 0 && (
                  <div className="mt-2">
                    <div className="text-xs font-semibold text-rose-700 mb-1">
                      Failures ({printResult.failures.length})
                    </div>
                    <div className="max-h-32 overflow-y-auto border border-rose-200 rounded text-[11px] bg-rose-50">
                      {printResult.failures.map((f, i) => (
                        <div key={i} className="px-2 py-1 border-b border-rose-200 last:border-0">
                          <span className="font-mono">{f.pack_barcode}</span>: {f.error}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setPrintResultDialogOpen(false)} data-testid="print-result-close">
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2026-05-14 (C) — Phase A "Pre-Print" prompt.
          Operator confirms the product printer + the product template
          before any TSPL is sent. Solves alignment issues that come
          from the auto-resolved template not matching the loaded roll.
          Continue is disabled until a printer is connected AND a
          template is selected. */}
      <Dialog
        open={phaseAOpen}
        onOpenChange={(open) => {
          if (!open && phaseAResolve) {
            phaseAResolve({ cancelled: true });
            setPhaseAResolve(null);
            setPhaseADevice(null);
          }
          setPhaseAOpen(open);
        }}
      >
        <DialogContent className="max-w-md" data-testid="phase-a-dialog">
          <DialogHeader>
            <DialogTitle>Confirm printer &amp; product template</DialogTitle>
          </DialogHeader>
          <div className="py-3 space-y-3 text-sm">
            <p className="text-gray-800">
              About to print product stickers for
              <span className="font-semibold"> {phaseAPackCount} pack(s)</span>
              {phaseAComboCount > 0 && (
                <> (including <span className="font-semibold">{phaseAComboCount} combo</span> — you'll be prompted to swap to the box-label printer after)</>
              )}.
            </p>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">Product Sticker Template</label>
              <Select value={phaseAProductTemplate} onValueChange={setPhaseAProductTemplate}>
                <SelectTrigger data-testid="phase-a-template-select">
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  {phaseAAvailableTemplates.map((n) => (
                    <SelectItem key={n} value={n} data-testid={`phase-a-template-option-${n}`}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">Product Printer</label>
              <div className="flex items-center gap-2">
                <Button
                  size="sm"
                  variant="outline"
                  disabled={phaseAConnecting}
                  onClick={async () => {
                    setPhaseAConnecting(true);
                    try {
                      const dev = await printerApi.requestUSBPrinter();
                      setPhaseADevice(dev);
                      toast.success(`Product printer connected: ${dev.productName || 'USB printer'}`);
                    } catch (e) {
                      toast.error(`Connect failed: ${e.message || e}`);
                    } finally {
                      setPhaseAConnecting(false);
                    }
                  }}
                  data-testid="phase-a-connect-printer-btn"
                  className="border-blue-300 text-blue-700 hover:bg-blue-50"
                >
                  {phaseAConnecting ? (
                    <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                  ) : (
                    <Printer className="w-3.5 h-3.5 mr-1" />
                  )}
                  {phaseADevice ? 'Re-select Printer' : 'Connect Printer'}
                </Button>
                {phaseADevice && (
                  <Badge
                    variant="outline"
                    className="bg-green-50 text-green-700 border-green-300"
                    data-testid="phase-a-printer-badge"
                  >
                    <CheckCircle className="w-3 h-3 mr-1" />
                    {phaseADevice.productName || 'USB printer'}
                  </Badge>
                )}
              </div>
            </div>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (phaseAResolve) phaseAResolve({ cancelled: true });
                setPhaseAResolve(null);
                setPhaseADevice(null);
                setPhaseAOpen(false);
              }}
              data-testid="phase-a-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              disabled={!phaseADevice || !phaseAProductTemplate}
              onClick={() => {
                if (phaseAResolve) {
                  phaseAResolve({
                    cancelled: false,
                    device: phaseADevice,
                    productTemplate: phaseAProductTemplate || null,
                  });
                }
                setPhaseAResolve(null);
                setUsbDevice(phaseADevice);
                setPhaseADevice(null);
                setPhaseAOpen(false);
              }}
              className="bg-blue-600 hover:bg-blue-700 text-white disabled:bg-gray-300"
              data-testid="phase-a-start-btn"
              title={
                !phaseADevice ? 'Connect a printer first'
                  : !phaseAProductTemplate ? 'Pick a product template' : ''
              }
            >
              Start Printing
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2026-05-14 — Two-Phase Combo Sticker Prompt.
          Combo packs need a product (inner) sticker AND a box sticker.
          Because the two roll sizes typically differ, we print all
          product stickers first, then prompt the operator to load the
          box-label roll, then print the box stickers. Cancel still
          marks every pack as restickered (per user spec).
          2026-05-14 (B): Operator MUST explicitly select the box-sticker
          printer in this prompt — Continue is disabled until a device
          is connected. */}
      <Dialog
        open={phaseDialogOpen}
        onOpenChange={(open) => {
          // Treat outside-click / Esc as Cancel to avoid stranding the pipeline.
          if (!open && phaseDialogResolve) {
            phaseDialogResolve({ cancelled: true });
            setPhaseDialogResolve(null);
            setPhaseBoxDevice(null);
          }
          setPhaseDialogOpen(open);
        }}
      >
        <DialogContent className="max-w-md" data-testid="phase-transition-dialog">
          <DialogHeader>
            <DialogTitle>Connect box-label printer to continue</DialogTitle>
          </DialogHeader>
          <div className="py-3 space-y-3 text-sm">
            <p className="text-gray-800">
              Product stickers printed. Load the box-label roll on the box-sticker printer,
              then <span className="font-semibold">connect it below</span> to print the box
              stickers for <span className="font-semibold">{phaseDialogComboCount} combo pack(s)</span>.
            </p>
            <div className="space-y-1">
              <label className="text-xs font-medium text-gray-600">Box Sticker Template</label>
              <Select value={phaseBoxTemplate} onValueChange={setPhaseBoxTemplate}>
                <SelectTrigger data-testid="phase-b-template-select">
                  <SelectValue placeholder="Select template" />
                </SelectTrigger>
                <SelectContent>
                  {phaseBoxAvailableTemplates.map((n) => (
                    <SelectItem key={n} value={n} data-testid={`phase-b-template-option-${n}`}>
                      {n}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center gap-2">
              <Button
                size="sm"
                variant="outline"
                disabled={phaseBoxConnecting}
                onClick={async () => {
                  setPhaseBoxConnecting(true);
                  try {
                    const dev = await printerApi.requestUSBPrinter();
                    setPhaseBoxDevice(dev);
                    toast.success(`Box printer connected: ${dev.productName || 'USB printer'}`);
                  } catch (e) {
                    toast.error(`Connect failed: ${e.message || e}`);
                  } finally {
                    setPhaseBoxConnecting(false);
                  }
                }}
                data-testid="phase-transition-connect-printer-btn"
                className="border-blue-300 text-blue-700 hover:bg-blue-50"
              >
                {phaseBoxConnecting ? (
                  <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" />
                ) : (
                  <Printer className="w-3.5 h-3.5 mr-1" />
                )}
                {phaseBoxDevice ? 'Re-select Printer' : 'Connect Box Printer'}
              </Button>
              {phaseBoxDevice && (
                <Badge
                  variant="outline"
                  className="bg-green-50 text-green-700 border-green-300"
                  data-testid="phase-transition-box-printer-badge"
                >
                  <CheckCircle className="w-3 h-3 mr-1" />
                  {phaseBoxDevice.productName || 'USB printer'}
                </Badge>
              )}
            </div>
            <p className="text-xs text-amber-700">
              Click Cancel to skip box stickers. Packs will still be marked re-stickered.
            </p>
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => {
                if (phaseDialogResolve) phaseDialogResolve({ cancelled: true });
                setPhaseDialogResolve(null);
                setPhaseBoxDevice(null);
                setPhaseDialogOpen(false);
              }}
              data-testid="phase-transition-cancel-btn"
            >
              Cancel
            </Button>
            <Button
              disabled={!phaseBoxDevice || !phaseBoxTemplate}
              onClick={() => {
                if (phaseDialogResolve) {
                  phaseDialogResolve({
                    cancelled: false,
                    device: phaseBoxDevice,
                    boxTemplate: phaseBoxTemplate || null,
                  });
                }
                setPhaseDialogResolve(null);
                // Persist the box-printer choice for subsequent print runs in this session.
                setUsbDevice(phaseBoxDevice);
                setPhaseBoxDevice(null);
                setPhaseDialogOpen(false);
              }}
              className="bg-emerald-600 hover:bg-emerald-700 text-white disabled:bg-gray-300"
              data-testid="phase-transition-continue-btn"
              title={
                !phaseBoxDevice ? 'Connect the box-label printer first'
                  : !phaseBoxTemplate ? 'Pick a box-sticker template' : ''
              }
            >
              Continue
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      <ErpConvertDialog slip={erpSlip} onClose={() => setErpSlip(null)} onDone={() => { setErpSlip(null); fetchPickSlips(); }} />
      <ErpStockPointsDialog open={showErpSettings} onClose={() => setShowErpSettings(false)} isAdmin={isAdmin} />

      {/* Admin — Reconcile orphaned picks (stuck PICKED on a cancelled/deleted slip) */}
      <Dialog open={showReconcile} onOpenChange={(o) => { if (!o) setShowReconcile(false); }}>
        <DialogContent className="max-w-lg" data-testid="reconcile-picks-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-[#2D5F4F]" />
              Fix Stuck Picks
            </DialogTitle>
            <DialogDescription>
              Release pieces wrongly stuck as "picked" on a cancelled or deleted slip so they can be picked again.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4">
            <p className="text-sm text-gray-600">
              Finds pieces stuck as <span className="font-semibold">PICKED</span> against a pick slip
              that was cancelled or deleted — so they wrongly show "already picked" and can't be
              re-picked. Pieces inside a live carton are never touched. Releasing them returns each
              piece to stock (or to the Put-Away queue if it has no bin).
            </p>

            {reconcileLoading && (
              <div className="flex items-center gap-2 text-sm text-gray-500" data-testid="reconcile-picks-loading">
                <Loader2 className="w-4 h-4 animate-spin" /> Scanning inventory…
              </div>
            )}

            {!reconcileLoading && reconcilePreview && !reconcileResult && (
              <div className="rounded-lg border bg-gray-50 p-4 space-y-2" data-testid="reconcile-picks-preview">
                <div className="flex items-center justify-between">
                  <span className="text-sm text-gray-600">Stuck pieces found</span>
                  <span className="text-2xl font-bold text-[#2D5F4F]" data-testid="reconcile-picks-count">
                    {reconcilePreview.releasable_rows ?? 0}
                  </span>
                </div>
                {reconcilePreview.excluded_in_live_box > 0 && (
                  <div className="text-xs text-gray-500">
                    {reconcilePreview.excluded_in_live_box} piece(s) are in a live carton and will be left alone.
                  </div>
                )}
                {reconcilePreview.by_reason && (
                  <div className="text-xs text-gray-500">
                    {Object.entries(reconcilePreview.by_reason).map(([k, v]) => (
                      <span key={k} className="mr-3">{k.toLowerCase()} slip: <b>{v}</b></span>
                    ))}
                  </div>
                )}
                {(reconcilePreview.sample || []).length > 0 && (
                  <div className="max-h-40 overflow-auto rounded border bg-white mt-2">
                    <table className="w-full text-xs">
                      <thead className="bg-gray-100 text-gray-600">
                        <tr><th className="text-left p-1.5">USN</th><th className="text-left p-1.5">SKU</th><th className="text-left p-1.5">Slip</th><th className="text-left p-1.5">Bin</th></tr>
                      </thead>
                      <tbody>
                        {reconcilePreview.sample.map((s, i) => (
                          <tr key={i} className="border-t" data-testid={`reconcile-sample-row-${i}`}>
                            <td className="p-1.5 font-mono">{s.usn}</td>
                            <td className="p-1.5">{s.sku}</td>
                            <td className="p-1.5">{s.slip || '—'}</td>
                            <td className="p-1.5">{s.bin || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                )}
              </div>
            )}

            {reconcileResult && (
              <div className="rounded-lg border border-green-200 bg-green-50 p-4 text-sm text-green-800" data-testid="reconcile-picks-result">
                <div className="flex items-center gap-2 font-semibold mb-1">
                  <CheckCircle className="w-4 h-4" /> Done
                </div>
                <div>Back to stock: <b>{reconcileResult.restored_to_stored ?? 0}</b></div>
                <div>Sent to Put-Away queue: <b>{reconcileResult.sent_to_put_away ?? 0}</b></div>
                <div className="text-xs text-green-700 mt-1">Batch: {reconcileResult.batch_tag}</div>
              </div>
            )}
          </div>
          <DialogFooter>
            {!reconcileResult ? (
              <>
                <Button variant="outline" onClick={() => setShowReconcile(false)} data-testid="reconcile-picks-cancel">
                  Cancel
                </Button>
                <Button
                  className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
                  disabled={reconcileLoading || reconcileApplying || !(reconcilePreview?.releasable_rows > 0)}
                  onClick={runReconcile}
                  data-testid="reconcile-picks-confirm"
                >
                  {reconcileApplying ? <Loader2 className="w-4 h-4 mr-2 animate-spin" /> : null}
                  Release {reconcilePreview?.releasable_rows ?? 0} piece(s)
                </Button>
              </>
            ) : (
              <Button onClick={() => setShowReconcile(false)} data-testid="reconcile-picks-close">Close</Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Admin — Add a piece to a closed carton */}
      <Dialog open={addBoxDialogOpen} onOpenChange={setAddBoxDialogOpen}>
        <DialogContent className="max-w-lg" data-testid="add-to-carton-dialog">
          <DialogHeader>
            <DialogTitle>Add piece to a closed carton</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              Slip <span className="font-semibold">{addBoxSlip?.pick_slip_number}</span>
              {addBoxSlip?.customer ? <> · {addBoxSlip.customer}</> : null}
            </div>
            {addBoxLoading ? (
              <div className="flex items-center justify-center py-8 text-gray-500">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />Loading cartons…
              </div>
            ) : addBoxList.length === 0 ? (
              <div className="py-6 text-center text-sm text-gray-500" data-testid="add-to-carton-no-boxes">
                No cartons packed on this slip yet.
              </div>
            ) : (
              <>
                <div>
                  <Label className="text-xs">Choose carton</Label>
                  <div className="mt-1 space-y-1 max-h-48 overflow-auto border rounded-md p-1" data-testid="add-to-carton-box-list">
                    {addBoxList.map((b) => (
                      <div
                        key={b.box_id}
                        className={`flex items-center gap-1 rounded ${addBoxSelectedId === b.box_id ? 'bg-emerald-50 ring-1 ring-emerald-400' : 'hover:bg-gray-50'}`}
                      >
                        <button
                          type="button"
                          onClick={() => setAddBoxSelectedId(b.box_id)}
                          className="flex-1 flex items-center justify-between px-3 py-2 text-left text-sm"
                          data-testid={`add-to-carton-box-${b.box_number}`}
                        >
                          <span className="font-medium">{b.box_number}{b.external_box_no ? ` · ${b.external_box_no}` : ''}</span>
                          <span className="text-gray-500">{b.total_qty} pcs</span>
                        </button>
                        {confirmDeleteBoxId === b.box_id ? (
                          <div className="flex items-center gap-1 pr-2 flex-shrink-0">
                            <span className="text-[11px] text-red-600">Delete?</span>
                            <Button size="sm" variant="ghost" className="h-7 px-2 text-red-600 hover:bg-red-50" disabled={deletingBoxId === b.box_id} onClick={() => handleDeleteBox(b.box_id)} data-testid={`add-to-carton-delete-confirm-${b.box_number}`}>
                              {deletingBoxId === b.box_id ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : 'Yes'}
                            </Button>
                            <Button size="sm" variant="ghost" className="h-7 px-2" onClick={() => setConfirmDeleteBoxId(null)} data-testid={`add-to-carton-delete-cancel-${b.box_number}`}>No</Button>
                          </div>
                        ) : (
                          <Button
                            size="icon"
                            variant="ghost"
                            className="w-8 h-8 mr-1 flex-shrink-0 text-red-500 hover:text-red-600 hover:bg-red-50"
                            title="Delete this carton (returns its pieces to stock)"
                            onClick={() => setConfirmDeleteBoxId(b.box_id)}
                            data-testid={`add-to-carton-delete-${b.box_number}`}
                          >
                            <Trash2 className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                    ))}
                  </div>
                </div>
                <div>
                  <Label className="text-xs">Scan / enter a fresh USN</Label>
                  <div className="flex gap-2 mt-1">
                    <Input
                      autoFocus
                      value={addBoxUsn}
                      onChange={(e) => setAddBoxUsn(e.target.value)}
                      onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); handleAddPieceToBox(); } }}
                      placeholder="USN code"
                      data-testid="add-to-carton-usn-input"
                    />
                    <Button
                      onClick={handleAddPieceToBox}
                      disabled={addBoxBusy || !addBoxSelectedId}
                      className="bg-emerald-600 hover:bg-emerald-700"
                      data-testid="add-to-carton-submit"
                    >
                      {addBoxBusy ? <Loader2 className="w-4 h-4 animate-spin" /> : 'Add'}
                    </Button>
                  </div>
                  <p className="text-xs text-gray-500 mt-1">
                    Only a fresh, available piece whose SKU is short on this slip can be added.
                    Already-used or dispatched USNs are rejected — re-sticker and scan a new one.
                    DC-linked slips are locked.
                  </p>
                </div>
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setAddBoxDialogOpen(false)} data-testid="add-to-carton-close">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>


      {/* DC Number Dialog */}
      <Dialog open={dcDialogOpen} onOpenChange={setDcDialogOpen}>
        <DialogContent className="max-w-lg max-h-[85vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>{dcNumber ? 'Edit' : 'Add'} DC Number</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>DC Number for Slip: {dcSlipNumber} <span className="text-red-500">*</span></Label>
              <Input
                value={dcNumber}
                onChange={(e) => setDcNumber(e.target.value)}
                placeholder="Enter DC number from accounting"
                className="mt-2"
                data-testid="dc-number-input"
              />
            </div>

            {/* Auto transport recommendation */}
            <div className="rounded-lg border border-[#E5DFD0] bg-[#FFFBF5] p-3" data-testid="transport-reco-panel">
              {loadingReco ? (
                <p className="text-sm text-gray-500 flex items-center gap-2"><Loader2 className="w-4 h-4 animate-spin" /> Finding the best transport…</p>
              ) : recommendation?.error ? (
                <p className="text-sm text-amber-700" data-testid="reco-error">{recommendation.error}</p>
              ) : recommendation ? (
                <div className="space-y-2">
                  <div className="flex items-center justify-between">
                    <span className="text-xs font-semibold text-[#1A1F2E]">Recommended Transport</span>
                    <span className="text-xs text-gray-500">
                      {recommendation.total_weight_kg} kg · {recommendation.total_pieces} pcs
                      {recommendation.state ? ` · ${recommendation.state}` : ''}
                    </span>
                  </div>
                  {recommendation.recommended_transport_id ? (
                    <>
                      <div className="flex items-center gap-2">
                        <CheckCircle className="w-4 h-4 text-[#2D5F4F]" />
                        <span className="font-semibold text-[#2D5F4F]" data-testid="reco-name">{recommendation.recommended_transport_name}</span>
                        <Badge className="bg-[#2D5F4F]/10 text-[#2D5F4F] border-0 text-[10px]">
                          {recommendation.selection_basis === 'customer_tagged' ? 'Customer default' : 'Best for state'}
                        </Badge>
                      </div>
                      <p className="text-xs text-gray-600" data-testid="reco-reason">{recommendation.reason}</p>
                      {(recommendation.candidates || []).length > 1 && (
                        <div className="mt-1 border rounded overflow-hidden">
                          <table className="w-full text-[11px]">
                            <thead className="bg-gray-50 text-gray-500">
                              <tr><th className="text-left px-2 py-1">Transport</th><th className="text-right px-2 py-1">Cost ₹</th><th className="text-right px-2 py-1">TAT</th><th className="text-right px-2 py-1">Score</th></tr>
                            </thead>
                            <tbody>
                              {recommendation.candidates.map((c) => (
                                <tr key={c.transport_id} className={c.transport_id === recommendation.recommended_transport_id ? 'bg-[#2D5F4F]/5' : ''} data-testid={`reco-cand-${c.transport_id}`}>
                                  <td className="px-2 py-1">{c.name}{' '}
                                    {c.cost_basis === 'history_per_kg' || c.cost_basis === 'history_per_piece' ? (
                                      <span className="text-green-600" title={`Real freight from outward records · ${c.rate_source || 'overall'} avg · ${c.rate_n || 0} shipments`}>· real</span>
                                    ) : (
                                      <span className="text-gray-400" title="Manual fallback rate (no freight history yet)">· manual</span>
                                    )}
                                  </td>
                                  <td className="px-2 py-1 text-right">{c.cost}{!c.eligible ? '*' : ''}</td>
                                  <td className="px-2 py-1 text-right">{c.expected_tat_days != null ? `${c.expected_tat_days}d` : '—'}</td>
                                  <td className="px-2 py-1 text-right">{c.score}</td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </div>
                      )}
                      {(recommendation.missing_weight_skus || []).length > 0 && (
                        <p className="text-[11px] text-amber-700" data-testid="reco-weight-warning">
                          ⚠ {recommendation.missing_weight_skus.length} SKU(s) missing weight — cost may be approximate.
                        </p>
                      )}
                      <p className="text-[11px] text-gray-400"><span className="text-green-600">real</span> = actual freight from Customer Outward records · <span className="text-gray-500">manual</span> = fallback rate. You can override the selection below.</p>
                    </>
                  ) : (
                    <p className="text-sm text-amber-700" data-testid="reco-none">
                      No transport rule matched{recommendation.state ? ` for ${recommendation.state}` : ''}. Tag states/customers in Transport Master, or pick manually below.
                    </p>
                  )}
                </div>
              ) : null}
            </div>

            <div>
              <Label>Transporter <span className="text-red-500">*</span></Label>
              <Input
                value={transporterSearch}
                onChange={(e) => {
                  setTransporterSearch(e.target.value);
                  // Clear selection if user is searching
                  if (selectedTransporter && e.target.value) {
                    const current = transporters.find(t => t.transport_id === selectedTransporter);
                    if (current && !current.name.toLowerCase().includes(e.target.value.toLowerCase())) {
                      setSelectedTransporter('');
                    }
                  }
                }}
                placeholder="Search transporter..."
                className="mt-2"
                data-testid="transporter-search-input"
              />
              <div className="max-h-40 overflow-y-auto border rounded-lg mt-2 bg-white">
                {filteredTransporters.length === 0 ? (
                  <p className="p-3 text-center text-gray-500 text-sm">
                    {transporters.length === 0 ? 'Loading transporters...' : 'No transporters found'}
                  </p>
                ) : (
                  filteredTransporters.map((t) => (
                    <div
                      key={t.transport_id}
                      onClick={() => {
                        setSelectedTransporter(t.transport_id);
                        setTransporterSearch(t.name);
                      }}
                      className={`p-3 cursor-pointer border-b last:border-b-0 transition-colors ${
                        selectedTransporter === t.transport_id
                          ? 'bg-[#2D5F4F]/10 border-l-4 border-l-[#2D5F4F]'
                          : 'hover:bg-gray-50'
                      }`}
                      data-testid={`transporter-option-${t.transport_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <span className="font-medium text-[#1A1F2E]">{t.name}</span>
                        {selectedTransporter === t.transport_id && (
                          <CheckCircle className="w-5 h-5 text-[#2D5F4F]" />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
              {selectedTransporter && (
                <p className="text-xs text-green-600 mt-1">
                  Selected: {transporters.find(t => t.transport_id === selectedTransporter)?.name}
                </p>
              )}
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDcDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleUpdateDc}
              disabled={!dcNumber.trim() || !selectedTransporter}
              className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
              data-testid="update-dc-btn"
            >
              Update DC
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Export Dialog */}
      <Dialog open={exportDialogOpen} onOpenChange={(open) => {
        setExportDialogOpen(open);
        if (!open) {
          setExportInfo(null);
        }
      }}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Export Completed Pick Slips</DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div>
              <Label>Start Date</Label>
              <Input
                type="date"
                value={exportStartDate}
                onChange={(e) => {
                  setExportStartDate(e.target.value);
                  setExportInfo(null);
                }}
                className="mt-1"
              />
            </div>
            <div>
              <Label>End Date</Label>
              <Input
                type="date"
                value={exportEndDate}
                onChange={(e) => {
                  setExportEndDate(e.target.value);
                  setExportInfo(null);
                }}
                className="mt-1"
              />
            </div>
            
            <div className="bg-gray-50 p-3 rounded-lg text-sm">
              <p className="font-medium text-[#1A1F2E] mb-1">CSV Columns:</p>
              <p className="text-[#5A5F6E]">Pick Slip No | Optional Barcode | Qty</p>
              <p className="text-xs text-gray-500 mt-2">Max 1 lakh rows per file. Large exports will be split into multiple files.</p>
            </div>
            
            {/* Show download buttons for multiple pages */}
            {exportInfo && exportInfo.total_pages > 1 && (
              <div className="bg-blue-50 p-3 rounded-lg">
                <p className="text-sm font-medium text-blue-800 mb-2">
                  {exportInfo.total_rows.toLocaleString()} rows found → {exportInfo.total_pages} files
                </p>
                <div className="flex flex-wrap gap-2">
                  {Array.from({ length: exportInfo.total_pages }, (_, i) => i + 1).map(page => (
                    <Button
                      key={page}
                      size="sm"
                      variant="outline"
                      onClick={() => downloadExportPage(page)}
                      className="text-blue-600 border-blue-200 hover:bg-blue-100"
                    >
                      <Download className="w-3 h-3 mr-1" />
                      File {page}
                    </Button>
                  ))}
                </div>
              </div>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setExportDialogOpen(false)}>
              Cancel
            </Button>
            <Button
              onClick={handleExport}
              disabled={exportLoading}
              className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
            >
              {exportLoading ? (
                'Preparing...'
              ) : (
                <>
                  <Download className="w-4 h-4 mr-2" />
                  {exportInfo ? 'Download All' : 'Export CSV'}
                </>
              )}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Pick Slip Dialog */}
      <Dialog open={cancelDialogOpen} onOpenChange={setCancelDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-red-600">
              <AlertTriangle className="w-5 h-5" />
              {forceCancel ? 'Force Cancel Pick Slip' : 'Cancel Pick Slip'}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className={`border rounded-lg p-4 ${forceCancel ? 'bg-orange-50 border-orange-200' : 'bg-red-50 border-red-200'}`}>
              <p className={`text-sm ${forceCancel ? 'text-orange-800' : 'text-red-800'}`}>
                You are about to cancel pick slip: <strong className="font-mono">{cancelSlipNumber}</strong>
              </p>
              {cancelSlipStatus && (
                <p className="text-xs text-gray-600 mt-1">
                  Current Status: <span className="font-semibold">{cancelSlipStatus}</span>
                </p>
              )}
              <p className={`text-xs mt-2 ${forceCancel ? 'text-orange-600' : 'text-red-600'}`}>
                All picked pieces will be moved to Put-Away queue for manual bin assignment.
              </p>
            </div>
            
            {/* Force Cancel Option - Admin Only for DC_LINKED or PACKED slips */}
            {isAdmin && ['DC_LINKED', 'PACKED'].includes(cancelSlipStatus) && (
              <div className="bg-orange-50 border border-orange-300 rounded-lg p-4">
                <div className="flex items-start gap-3">
                  <input
                    type="checkbox"
                    id="forceCancel"
                    checked={forceCancel}
                    onChange={(e) => setForceCancel(e.target.checked)}
                    className="mt-1 h-4 w-4 text-orange-600 border-orange-300 rounded focus:ring-orange-500"
                  />
                  <div>
                    <Label htmlFor="forceCancel" className="text-orange-800 font-semibold cursor-pointer">
                      Force Cancel (Admin Override)
                    </Label>
                    <p className="text-xs text-orange-700 mt-1">
                      {cancelSlipStatus === 'DC_LINKED' && 'This will unlink the DC and cancel the pick slip.'}
                      {cancelSlipStatus === 'PACKED' && 'This will cancel the packed slip and move pieces to put-away queue.'}
                    </p>
                    <p className="text-xs text-orange-600 mt-1 font-medium">
                      ⚠️ This action cannot be undone. Use with caution.
                    </p>
                  </div>
                </div>
              </div>
            )}
            
            <div>
              <Label className="text-red-700">
                Cancellation Reason <span className="text-red-500">*</span>
              </Label>
              <Textarea
                value={cancellationReason}
                onChange={(e) => setCancellationReason(e.target.value)}
                placeholder="Please provide a reason for cancellation (mandatory)"
                className="mt-2 min-h-[100px]"
                data-testid="cancellation-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setCancelDialogOpen(false)}
              disabled={cancelSubmitting}
            >
              Go Back
            </Button>
            <Button
              onClick={handleCancelPickSlip}
              disabled={cancelSubmitting || !cancellationReason.trim() || (['DC_LINKED', 'PACKED'].includes(cancelSlipStatus) && !forceCancel)}
              className={forceCancel ? 'bg-orange-600 hover:bg-orange-700 text-white' : 'bg-red-600 hover:bg-red-700 text-white'}
              data-testid="confirm-cancel-btn"
            >
              {cancelSubmitting ? 'Cancelling...' : forceCancel ? 'Force Cancel' : 'Confirm Cancel'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Success Confirmation Dialog (for printout) */}
      <Dialog open={successDialogOpen} onOpenChange={setSuccessDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-green-600">
              <CheckCircle className="w-5 h-5" />
              Pick Slip {cancelledSlipInfo?.forceCancel ? 'Force ' : ''}Cancelled Successfully
            </DialogTitle>
          </DialogHeader>
          <div className="py-6">
            <div className={`border rounded-lg p-6 text-center ${cancelledSlipInfo?.forceCancel ? 'bg-orange-50 border-orange-200' : 'bg-green-50 border-green-200'}`}>
              <p className="text-sm text-gray-600 mb-2">Pick Slip Number:</p>
              <p className="text-2xl font-bold font-mono text-[#1A1F2E] mb-4">
                {cancelledSlipInfo?.slipNumber}
              </p>
              {cancelledSlipInfo?.forceCancel && (
                <div className="bg-orange-100 rounded-lg p-2 mb-3">
                  <p className="text-xs text-orange-800 font-medium">
                    ⚠️ Force Cancelled by Admin
                  </p>
                  {cancelledSlipInfo?.previousStatus && (
                    <p className="text-xs text-orange-700">
                      Previous Status: {cancelledSlipInfo.previousStatus}
                    </p>
                  )}
                  {cancelledSlipInfo?.unlinkedDC && (
                    <p className="text-xs text-orange-700">
                      Unlinked from DC: {cancelledSlipInfo.unlinkedDC}
                    </p>
                  )}
                </div>
              )}
              <div className={`border-t pt-4 mt-4 ${cancelledSlipInfo?.forceCancel ? 'border-orange-200' : 'border-green-200'}`}>
                <p className="text-sm text-gray-600">
                  <span className="font-medium">{cancelledSlipInfo?.pendingPutAwayCount || 0}</span> piece(s) moved to Put-Away Queue
                </p>
                <p className="text-xs text-gray-500 mt-1">
                  Cancelled by: {cancelledSlipInfo?.cancelledBy}
                </p>
              </div>
            </div>
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 mt-4">
              <p className="text-xs text-amber-800 text-center">
                <AlertTriangle className="w-4 h-4 inline mr-1" />
                Go to <strong>FG Put-Away</strong> to assign bins for cancelled pieces
              </p>
            </div>
          </div>
          <DialogFooter className="justify-center">
            <Button
              onClick={() => {
                setSuccessDialogOpen(false);
                setCancelledSlipInfo(null);
              }}
              className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white px-8"
              data-testid="close-success-dialog"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Repair Result Dialog */}
      <Dialog open={repairDialogOpen} onOpenChange={setRepairDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Wrench className="w-5 h-5 text-amber-600" />
              Status Repair Results
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {repairResult && (
              <>
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="grid grid-cols-2 gap-4 text-center">
                    <div>
                      <p className="text-2xl font-bold text-[#2D5F4F]">{repairResult.fixed_count}</p>
                      <p className="text-sm text-gray-600">Slips Fixed</p>
                    </div>
                    <div>
                      <p className="text-2xl font-bold text-gray-600">{repairResult.total_checked}</p>
                      <p className="text-sm text-gray-600">Total Checked</p>
                    </div>
                  </div>
                </div>
                
                {repairResult.fixed_slips && repairResult.fixed_slips.length > 0 && (
                  <div className="border rounded-lg overflow-hidden">
                    <div className="bg-gray-100 px-3 py-2 text-sm font-medium text-gray-700">
                      Fixed Pick Slips
                    </div>
                    <div className="max-h-64 overflow-y-auto">
                      {repairResult.fixed_slips.map((slip, idx) => (
                        <div key={idx} className="px-3 py-2 border-b last:border-b-0 text-sm">
                          <div className="flex justify-between items-center">
                            <span className="font-mono font-medium">{slip.slip_number}</span>
                            <span className="text-xs">
                              <span className="text-red-500">{slip.old_status}</span>
                              <span className="mx-2">→</span>
                              <span className="text-green-600 font-medium">{slip.new_status}</span>
                            </span>
                          </div>
                          {slip.reason && (
                            <p className="text-xs text-gray-500 mt-1">{slip.reason}</p>
                          )}
                        </div>
                      ))}
                    </div>
                  </div>
                )}
                
                {repairResult.fixed_count === 0 && (
                  <div className="text-center py-4">
                    <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-2" />
                    <p className="text-gray-600">All pick slip statuses are correct!</p>
                    <p className="text-sm text-gray-500 mt-1">No repairs were needed.</p>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button
              onClick={() => setRepairDialogOpen(false)}
              className="bg-[#2D5F4F] hover:bg-[#234A3D] text-white"
            >
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reopen Pick Slip Dialog */}
      <Dialog open={reopenDialogOpen} onOpenChange={setReopenDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <Edit className="w-5 h-5" />
              Reopen Pick Slip
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-amber-50 border border-amber-200 rounded-lg p-4">
              <p className="text-sm text-amber-800">
                You are about to reopen pick slip: <strong className="font-mono">{reopenSlipNumber}</strong>
              </p>
              <p className="text-xs text-amber-600 mt-2">
                This will change the status back to IN_PROGRESS so the picker can continue or correct the picking.
              </p>
            </div>
            
            <div>
              <Label className="text-gray-700">
                Reason for reopening (optional)
              </Label>
              <Textarea
                value={reopenReason}
                onChange={(e) => setReopenReason(e.target.value)}
                placeholder="e.g., Picker wrongly completed, items still pending..."
                className="mt-2 min-h-[80px]"
                data-testid="reopen-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setReopenDialogOpen(false)}
              disabled={reopenSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReopenPickSlip}
              disabled={reopenSubmitting}
              className="bg-amber-600 hover:bg-amber-700 text-white"
              data-testid="confirm-reopen-btn"
            >
              {reopenSubmitting ? 'Reopening...' : 'Confirm Reopen'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* 2026-05-14 — Re-arm Pack Prints Dialog (Admin only) */}
      <Dialog open={reArmDialogOpen} onOpenChange={setReArmDialogOpen}>
        <DialogContent className="max-w-md" data-testid="rearm-prints-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-600">
              <RotateCcw className="w-5 h-5" />
              Re-arm Pack Prints
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <p className="text-sm text-indigo-900">
                Slip: <strong className="font-mono">{reArmSlipNumber}</strong>
              </p>
              <p className="text-xs text-indigo-800 mt-2 leading-relaxed">
                This clears <code>restickered_at</code> on every pack that was re-stickered for this
                slip so the <strong>Print Sticker</strong> per-SKU buttons treat them as fresh.
              </p>
              <ul className="text-[11px] text-indigo-800 mt-2 list-disc list-inside space-y-0.5">
                <li>Slip status is <strong>not</strong> changed.</li>
                <li>Pack status stays <strong>PICKED</strong>.</li>
                <li>USNs minted in the previous run remain live — a fresh re-sticker run will mint another new USN on top.</li>
                <li>An audit row is pushed to the slip's <code>status_history</code>.</li>
              </ul>
            </div>

            <div>
              <Label className="text-gray-700">Reason (optional)</Label>
              <Textarea
                value={reArmReason}
                onChange={(e) => setReArmReason(e.target.value)}
                placeholder="e.g., Printer jammed mid-batch — half stickers missing…"
                className="mt-2 min-h-[70px]"
                data-testid="rearm-prints-reason-input"
              />
            </div>

            {/* Result panel shown after a successful re-arm. */}
            {reArmResult && (
              <div
                className="bg-emerald-50 border border-emerald-200 rounded-lg p-3 text-xs text-emerald-800 space-y-1"
                data-testid="rearm-prints-result"
              >
                <div className="font-medium">{reArmResult.message}</div>
                {(reArmResult.by_sku || []).length > 0 && (
                  <div>
                    <div className="font-semibold mt-1">Re-armed by SKU:</div>
                    <ul className="list-disc list-inside">
                      {reArmResult.by_sku.map((row) => (
                        <li key={row.sku_code}>
                          <span className="font-mono">{row.sku_code}</span> — {row.pack_count} pack(s)
                        </li>
                      ))}
                    </ul>
                  </div>
                )}
              </div>
            )}
          </div>
          <DialogFooter>
            <Button
              variant="outline"
              onClick={() => setReArmDialogOpen(false)}
              disabled={reArmSubmitting}
              data-testid="rearm-prints-close-btn"
            >
              {reArmResult ? 'Close' : 'Cancel'}
            </Button>
            {!reArmResult && (
              <Button
                onClick={handleReArmPackPrints}
                disabled={reArmSubmitting}
                className="bg-indigo-600 hover:bg-indigo-700 text-white"
                data-testid="confirm-rearm-prints-btn"
              >
                {reArmSubmitting ? 'Re-arming…' : 'Confirm Re-arm Prints'}
              </Button>
            )}
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassign Pick Slip Dialog */}
      <Dialog open={reassignDialogOpen} onOpenChange={setReassignDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-indigo-600">
              <UserCheck className="w-5 h-5" />
              Reassign Pick Slip
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            <div className="bg-indigo-50 border border-indigo-200 rounded-lg p-4">
              <p className="text-sm text-indigo-800">
                Reassigning pick slip: <strong className="font-mono">{reassignSlipNumber}</strong>
              </p>
              <div className="mt-2 grid grid-cols-2 gap-2 text-xs">
                <div>
                  <span className="text-gray-500">Current Picker:</span>
                  <span className="ml-1 font-medium text-indigo-700">{reassignSlipInfo?.currentPicker || 'Unassigned'}</span>
                </div>
                <div>
                  <span className="text-gray-500">Status:</span>
                  <span className="ml-1">{getStatusBadge(reassignSlipInfo?.status)}</span>
                </div>
                <div>
                  <span className="text-gray-500">Total Items:</span>
                  <span className="ml-1 font-medium">{reassignSlipInfo?.totalItems}</span>
                </div>
                <div>
                  <span className="text-gray-500">Total Qty:</span>
                  <span className="ml-1 font-medium">{reassignSlipInfo?.totalQty}</span>
                </div>
              </div>
              <p className="text-xs text-indigo-600 mt-3">
                <AlertTriangle className="w-3 h-3 inline mr-1" />
                New picker will continue from where the previous picker left off. Already picked items will be preserved.
              </p>
            </div>
            
            <div>
              <Label className="text-gray-700">
                Select New Picker <span className="text-red-500">*</span>
              </Label>
              <Input
                value={staffSearchTerm}
                onChange={(e) => setStaffSearchTerm(e.target.value)}
                placeholder="Search by name or ID..."
                className="mt-2 mb-2"
                data-testid="staff-search-input"
              />
              <div className="max-h-48 overflow-y-auto border rounded-lg">
                {filteredStaffList.length === 0 ? (
                  <p className="p-3 text-center text-gray-500 text-sm">
                    {staffList.length === 0 ? 'Loading staff...' : 'No staff found'}
                  </p>
                ) : (
                  filteredStaffList.map((staff) => (
                    <div
                      key={staff.staff_id}
                      onClick={() => setSelectedNewPicker(staff.staff_id)}
                      className={`p-3 cursor-pointer border-b last:border-b-0 transition-colors ${
                        selectedNewPicker === staff.staff_id
                          ? 'bg-indigo-100 border-l-4 border-l-indigo-500'
                          : 'hover:bg-gray-50'
                      } ${
                        staff.staff_id === reassignSlipInfo?.currentPickerId
                          ? 'opacity-50'
                          : ''
                      }`}
                      data-testid={`staff-option-${staff.staff_id}`}
                    >
                      <div className="flex items-center justify-between">
                        <div>
                          <p className="font-medium text-[#1A1F2E]">{staff.staff_name}</p>
                          <p className="text-xs text-gray-500">{staff.staff_id}</p>
                        </div>
                        {staff.staff_id === reassignSlipInfo?.currentPickerId && (
                          <Badge variant="outline" className="text-xs">Current</Badge>
                        )}
                        {selectedNewPicker === staff.staff_id && (
                          <CheckCircle className="w-5 h-5 text-indigo-600" />
                        )}
                      </div>
                    </div>
                  ))
                )}
              </div>
            </div>
            
            <div>
              <Label className="text-gray-700">
                Reason for reassignment (optional)
              </Label>
              <Textarea
                value={reassignReason}
                onChange={(e) => setReassignReason(e.target.value)}
                placeholder="e.g., Picker is absent, emergency reassignment..."
                className="mt-2 min-h-[60px]"
                data-testid="reassign-reason-input"
              />
            </div>
          </div>
          <DialogFooter>
            <Button 
              variant="outline" 
              onClick={() => setReassignDialogOpen(false)}
              disabled={reassignSubmitting}
            >
              Cancel
            </Button>
            <Button
              onClick={handleReassignPickSlip}
              disabled={reassignSubmitting || !selectedNewPicker || selectedNewPicker === reassignSlipInfo?.currentPickerId}
              className="bg-indigo-600 hover:bg-indigo-700 text-white"
              data-testid="confirm-reassign-btn"
            >
              {reassignSubmitting ? 'Reassigning...' : 'Confirm Reassign'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reassignment History Dialog */}
      <Dialog open={historyDialogOpen} onOpenChange={setHistoryDialogOpen}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-gray-700">
              <History className="w-5 h-5" />
              Reassignment History
            </DialogTitle>
          </DialogHeader>
          <div className="py-4">
            {reassignmentHistory && (
              <>
                <div className="bg-gray-50 rounded-lg p-4 mb-4">
                  <div className="flex justify-between items-center">
                    <div>
                      <p className="text-sm text-gray-500">Pick Slip</p>
                      <p className="font-mono font-bold text-[#1A1F2E]">{reassignmentHistory.slip_number}</p>
                    </div>
                    <div className="text-right">
                      <p className="text-sm text-gray-500">Current Picker</p>
                      <p className="font-medium text-[#1A1F2E]">{reassignmentHistory.current_picker}</p>
                    </div>
                  </div>
                  <div className="mt-2 text-center">
                    <Badge variant="outline">{reassignmentHistory.reassignment_count} reassignment(s)</Badge>
                  </div>
                </div>
                
                {reassignmentHistory.history?.length === 0 ? (
                  <p className="text-center text-gray-500 py-4">No reassignment history</p>
                ) : (
                  <div className="space-y-3 max-h-64 overflow-y-auto">
                    {reassignmentHistory.history?.map((entry, idx) => (
                      <div key={idx} className="bg-white border rounded-lg p-3">
                        <div className="flex items-center gap-2 mb-2">
                          <Users className="w-4 h-4 text-gray-400" />
                          <span className="text-sm text-gray-600">
                            {entry.previous_picker_name || 'Unassigned'} → <strong className="text-indigo-600">{entry.new_picker_name}</strong>
                          </span>
                        </div>
                        <div className="grid grid-cols-2 gap-2 text-xs text-gray-500">
                          <div>
                            <span className="font-medium">By:</span> {entry.reassigned_by_name}
                          </div>
                          <div>
                            <span className="font-medium">At:</span> {new Date(entry.reassigned_at).toLocaleString()}
                          </div>
                        </div>
                        {entry.reason && (
                          <p className="text-xs text-gray-600 mt-2 italic">
                            Reason: {entry.reason}
                          </p>
                        )}
                        {entry.progress_at_reassignment && (
                          <div className="mt-2 text-xs bg-gray-50 rounded p-2">
                            <span className="text-gray-500">Progress at reassignment:</span>
                            <span className="ml-1 font-medium">
                              {entry.progress_at_reassignment.qty_picked}/{entry.progress_at_reassignment.total_qty} qty picked
                            </span>
                          </div>
                        )}
                      </div>
                    ))}
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button onClick={() => setHistoryDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Cancel Single USN Dialog */}
      <Dialog open={cancelUSNDialogOpen} onOpenChange={setCancelUSNDialogOpen}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-amber-600">
              <List className="w-5 h-5" />
              Picked USNs - {cancelUSNSlipNumber}
            </DialogTitle>
          </DialogHeader>
          <div className="py-4 space-y-4">
            {loadingUSNs ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-amber-500"></div>
              </div>
            ) : (
              <>
                {/* Search and Select All controls */}
                <div className="space-y-2">
                  <div className="relative">
                    <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-gray-400" />
                    <Input
                      value={usnSearchTerm}
                      onChange={(e) => setUsnSearchTerm(e.target.value)}
                      placeholder="Search USN, barcode, or design..."
                      className="pl-10"
                      data-testid="usn-search-input"
                    />
                  </div>
                  <div className="flex items-center justify-between">
                    <div className="flex items-center gap-2">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={selectAllUSNs}
                        disabled={filteredPickedUSNs.length === 0}
                        className="text-xs"
                      >
                        <CheckSquare className="w-3 h-3 mr-1" />
                        Select All ({filteredPickedUSNs.length})
                      </Button>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={deselectAllUSNs}
                        disabled={selectedUSNsToCancel.length === 0}
                        className="text-xs"
                      >
                        <Square className="w-3 h-3 mr-1" />
                        Deselect All
                      </Button>
                    </div>
                    {selectedUSNsToCancel.length > 0 && (
                      <Badge className="bg-amber-100 text-amber-700">
                        {selectedUSNsToCancel.length} selected
                      </Badge>
                    )}
                  </div>
                </div>

                {/* Picked USNs List */}
                <div>
                  <div className="flex items-center justify-between mb-2">
                    <Label className="text-gray-700 font-medium">
                      Picked USNs ({filteredPickedUSNs.length})
                    </Label>
                    <Badge variant="outline" className="bg-green-50 text-green-700">
                      Active
                    </Badge>
                  </div>
                  {filteredPickedUSNs.length === 0 ? (
                    <p className="text-center py-4 text-gray-500 bg-gray-50 rounded-lg">
                      {usnSearchTerm ? 'No USNs match your search' : 'No picked USNs available'}
                    </p>
                  ) : (
                    <div className="max-h-60 overflow-y-auto border rounded-lg">
                      {filteredPickedUSNs.map((usn, idx) => {
                        const isSelected = selectedUSNsToCancel.some(u => u.usn_code === usn.usn_code);
                        return (
                          <div
                            key={idx}
                            onClick={() => toggleUSNSelection(usn)}
                            className={`p-3 border-b last:border-b-0 cursor-pointer transition-colors ${
                              isSelected
                                ? 'bg-amber-100 border-l-4 border-l-amber-500'
                                : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-center gap-3">
                              <Checkbox
                                checked={isSelected}
                                onCheckedChange={() => toggleUSNSelection(usn)}
                                className="data-[state=checked]:bg-amber-500 data-[state=checked]:border-amber-500"
                              />
                              <div className="flex-1">
                                <p className="font-mono font-medium text-[#1A1F2E]">{usn.usn_code}</p>
                                <p className="text-xs text-gray-500">
                                  {usn.item_barcode} • {usn.design_name}
                                </p>
                                <p className="text-xs text-gray-400">
                                  Bin: {usn.bin_code || 'N/A'} • Picked: {usn.picked_at ? new Date(usn.picked_at).toLocaleString() : 'N/A'}
                                  {usn.box_number && (
                                    <span className="text-amber-700" data-testid={`usn-box-${usn.usn_code}`}> • Carton: {usn.box_number}{usn.external_box_no ? ` (${usn.external_box_no})` : ''}</span>
                                  )}
                                </p>
                              </div>
                              {isSelected && (
                                <CheckCircle className="w-5 h-5 text-amber-600" />
                              )}
                            </div>
                          </div>
                        );
                      })}
                    </div>
                  )}
                </div>

                {/* Cancel USN Form - only show when USNs are selected */}
                {selectedUSNsToCancel.length > 0 && (
                  <div className="bg-red-50 border border-red-200 rounded-lg p-4 space-y-3">
                    <div className="flex items-center gap-2 text-red-700">
                      <Trash2 className="w-4 h-4" />
                      <span className="font-medium">
                        Cancel {selectedUSNsToCancel.length} Selected USN{selectedUSNsToCancel.length > 1 ? 's' : ''}
                      </span>
                    </div>
                    {selectedUSNsToCancel.length <= 3 && (
                      <div className="bg-white rounded p-2 border space-y-1">
                        {selectedUSNsToCancel.map((usn, idx) => (
                          <p key={idx} className="text-sm">
                            <span className="font-mono font-medium">{usn.usn_code}</span>
                            <span className="text-gray-500 text-xs ml-2">{usn.item_barcode}</span>
                          </p>
                        ))}
                      </div>
                    )}
                    {selectedUSNsToCancel.length > 3 && (
                      <div className="bg-white rounded p-2 border">
                        <p className="text-sm text-gray-600">
                          {selectedUSNsToCancel.length} USNs selected for cancellation
                        </p>
                      </div>
                    )}
                    <div>
                      <Label className="text-gray-700">
                        Cancellation Reason <span className="text-red-500">*</span>
                      </Label>
                      <Select value={cancelUSNReason} onValueChange={setCancelUSNReason}>
                        <SelectTrigger className="mt-1" data-testid="cancel-usn-reason-select">
                          <SelectValue placeholder="Select reason..." />
                        </SelectTrigger>
                        <SelectContent>
                          {USN_CANCEL_REASONS.map((reason) => (
                            <SelectItem key={reason} value={reason}>
                              {reason}
                            </SelectItem>
                          ))}
                        </SelectContent>
                      </Select>
                      {cancelUSNReason === 'Other' && (
                        <Textarea
                          value={cancelUSNCustomReason}
                          onChange={(e) => setCancelUSNCustomReason(e.target.value)}
                          placeholder="Please specify the reason..."
                          className="mt-2"
                          data-testid="cancel-usn-custom-reason"
                        />
                      )}
                    </div>
                    <Button
                      onClick={handleCancelUSN}
                      disabled={cancelUSNSubmitting || (!cancelUSNReason || (cancelUSNReason === 'Other' && !cancelUSNCustomReason.trim()))}
                      className="w-full bg-red-600 hover:bg-red-700 text-white"
                      data-testid="confirm-cancel-usn-btn"
                    >
                      {cancelUSNSubmitting ? 'Cancelling...' : `Cancel ${selectedUSNsToCancel.length} USN${selectedUSNsToCancel.length > 1 ? 's' : ''}`}
                    </Button>
                    <p className="text-xs text-red-600" data-testid="cancel-usn-note">
                      <AlertTriangle className="w-3 h-3 inline mr-1" />
                      {usnPickPack
                        ? 'USN(s) will be taken out of their carton, the slip line becomes short, and the piece goes to the Cancel Put-Away Queue. Reprint the carton label.'
                        : 'USN(s) will be moved to Cancel Put-Away Queue for re-shelving'}
                    </p>
                  </div>
                )}

                {/* Cancelled USNs History */}
                {cancelledUSNs.length > 0 && (
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <Label className="text-gray-700 font-medium">
                        Cancelled USNs ({cancelledUSNs.length})
                      </Label>
                      <Badge variant="outline" className="bg-red-50 text-red-700">
                        History
                      </Badge>
                    </div>
                    <div className="max-h-40 overflow-y-auto border rounded-lg bg-gray-50">
                      {cancelledUSNs.map((usn, idx) => (
                        <div key={idx} className="p-3 border-b last:border-b-0 bg-white/50">
                          <div className="flex items-center justify-between">
                            <div>
                              <p className="font-mono font-medium text-gray-500 line-through">{usn.usn_code}</p>
                              <p className="text-xs text-gray-400">{usn.item_barcode}</p>
                            </div>
                            <div className="text-right">
                              <p className="text-xs text-red-600">{usn.cancellation_reason}</p>
                              <p className="text-xs text-gray-400">
                                by {usn.cancelled_by_name} • {new Date(usn.cancelled_at).toLocaleDateString()}
                              </p>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                )}
              </>
            )}
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setCancelUSNDialogOpen(false)}>
              Close
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default FGPickSlips;
